// COP — the imperative shell wiring. NO React here.
//
// Three planes, assembled imperatively:
//   - render-engine (CONTRACT:C2): created with a deck.gl WebGL2 backend bound
//     to a MapLibre map. Owns the single rAF render loop.
//   - capability-host (CONTRACT:C3): assembles a HostContext (engine + tokens +
//     policy + data bus) and activates the sample tracks capability.
//   - design-tokens (CONTRACT:C4): the dark tactical theme drives :root vars and
//     the affiliation symbology.
// React (main.tsx) only mounts the shell/HUD/panels and reads status via the
// callbacks this module exposes — it never touches the render loop.

import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";

import { createRenderEngine } from "../../../../packages/render-engine/src/index.ts";
import { CapabilityHost } from "../../../../packages/capability-host/src/index.ts";
import type { PanelContribution } from "../../../../packages/capability-host/src/index.ts";
import { tokens, THEMES } from "../../../../packages/design-tokens/src/index.ts";
import type {
  PolicyRequest,
  PolicyDecision,
} from "../../../../packages/contracts-ts/src/index.ts";
import type { CanonicalEnvelope, Affiliation } from "../../../../packages/schema/ts/envelope.ts";

import { DeckBackend } from "./deckBackend.ts";
import { EnvelopeBus } from "./envelopeBus.ts";
import { startFeed, type FeedHandle, type FeedMode } from "./feed.ts";
import { scenarioList, activeScenarioId, DEFAULT_SCENARIO_ID } from "./scenario.ts";
import { applyTheme, affiliationColorsFor, basemapUrlForProfile } from "./theme.ts";
import { getSettings, getSettingsStore } from "../../../../packages/local-settings/src/index.ts";
import { createAlertAcks } from "./alertAcks.ts";
import type { PolicyBundle, SubjectClaims } from "../../../../packages/abac-pdp/src/index.ts";
// THE ONE canonical classification ladder (M3 fix): import the single shared CLASS_ORDER
// + CLASS_LEVELS from abac-pdp rather than re-declaring a local lattice that could drift.
import { CLASS_ORDER, CLASS_LEVELS } from "../../../../packages/abac-pdp/src/index.ts";
// Informal persona metadata (real display names) — display/audit legibility only,
// NEVER an ABAC claim. The username remains the security subject everywhere.
import { displayNameFor } from "./personas.ts";
import { createTracksCapability } from "../capabilities/tracksCapability.ts";
import {
  createMultiIntCapability,
  MULTI_INT_CAP_ID,
  MULTI_INT_MANIFEST,
} from "../capabilities/multiIntCapability.ts";
import { createDensityCapability, DENSITY_CAP_ID } from "../capabilities/densityCapability.ts";
import { createStressCapability, STRESS_CAP_ID } from "../capabilities/stressCapability.ts";
import { createCopilotCapability } from "../capabilities/copilotCapability.ts";
import { COPILOT_MANIFEST, COPILOT_CAPABILITY_ID } from "../capabilities/copilotManifest.ts";
import { createAdsbCapability, ADSB_CAP_ID, ADSB_MANIFEST } from "../capabilities/adsbCapability.ts";
import { createUsgsCapability, USGS_CAP_ID, USGS_MANIFEST } from "../capabilities/usgsCapability.ts";
import { createNwsCapability, NWS_CAP_ID, NWS_MANIFEST } from "../capabilities/nwsCapability.ts";
import type { CopilotApi } from "../capabilities/copilotApi.ts";
import { initKas, setKasToken, decryptTdf, startKasHeartbeat, loadSeededObjects, tokenState, canSilentlyRenew, onTokenRenewed, type SeededObject } from "./kas.ts";
import { createChatKeyAccess } from "./chatKeyAccess.ts";
import {
  createChatCapability,
  isChatEnvelope,
  CHAT_CAPABILITY_ID,
  type ChatApi,
  type BridgeAuditEvent,
} from "../capabilities/chatCapability.ts";
import { createSessionSigner, type MessageSigner } from "../../../../packages/cap-chat/src/messages.ts";
import { createCopPdp, annotateForRender } from "./pdp.ts";
// THE VIEW-AS DESCOPE SEAM (the ONE place apps/cop steps a subject DOWN — never narrowHeld
// directly). The multi-dim "view-as" overlay generalizes the single-axis query level: any
// axis (classification ceiling, releasability, SCI, caveat) can be voluntarily narrowed, and
// descopedClaimsFor mirrors classification→classification_level + strips the legacy key + never
// normalizes (see descope.ts). specDominatedByHeld tells the UI whether the preview reproduces
// the named target EXACTLY (held ⊇ spec) or is stricter than named.
import { descopedClaimsFor, specDominatedByHeld, type DescopeSpec } from "./descope.ts";
import { createVisibleProjection, type VisibleProjection, type VisibleDelta } from "./visibleProjection.ts";
import { decideForResource } from "./seamPolicy.ts";
import { makeChatEmit } from "./emitClient.ts";
import {
  buildMarkerEnvelope,
  resolveMarkerClassification,
  MarkerClassificationError,
} from "../capabilities/markerCapability.ts";
// TRUE OWN-POSITION sharing (double-consent, fail-closed) + the CALL-FOR-EVAC flow.
import {
  createOwnPositionSharer,
  showShareLocationConsentDialog,
  readPresenceSettings,
} from "./ownPosition.ts";
import { resolveEvacLocation, showEvacComposerDialog } from "./evacRequest.ts";
import {
  buildEvacEnvelope,
  placeholderSelfLocation,
  type SelfLocation,
} from "../capabilities/copilotEvac.ts";
import { effectiveCap, DENY_ORDER, type ActorCapTerms } from "./actor.ts";
import { pushToast } from "../components/Toasts.tsx";
import type {
  VerdictState,
  LayerRow,
  CatalogGroup,
  CensusCounts,
  CartoucheState,
  CursorReadout,
  PictureSummary,
  PictureStat,
  UnderstudyEntry,
  QueryResult,
  ActiveFilter,
  LayoutRecipe,
  AuditVerdict,
  AuditRow,
  AlertRow,
} from "./shellContract.ts";

// Re-export the shared shell contract so GROUP A components can import the named
// types from a single host entrypoint (`host/cop.ts`) OR directly from
// `host/shellContract.ts` — both resolve to the same plain types. Spec:
// docs/ux/SHELL-COMPOSITION.md §3.
export type {
  VerdictFace,
  VerdictUrgency,
  VerdictReason,
  VerdictState,
  LayerHealth,
  LayerRow,
  CatalogLeaf,
  CatalogGroup,
  CensusCounts,
  CartoucheState,
  CursorReadout,
  PictureSummaryRow,
  PictureSummary,
  PictureStat,
  PictureStatTone,
  UnderstudyEntry,
  QueryRung,
  QueryResult,
  ActiveFilter,
  LayoutRecipe,
  AuditVerdict,
  AuditRow,
  AlertRow,
} from "./shellContract.ts";

/** The logged-in identity the COP boots against: the live policy bundle (from
 *  TDFLite /api/policy-attrs) + the user's JWT claims (the ABAC subject) + a label. */
export interface CopIdentity {
  bundle: PolicyBundle;
  claims: SubjectClaims;
  /** The logged-in persona's JWT — the rewrap subject for the client-side KAS (I5). */
  token: string;
  label: string;
}

// Dark "instrument" basemap. CARTO dark-matter (no API key) reads as a tactical
// scope, not a daytime road map. If it ever fails to load we still boot (the
// feed/engine don't depend on the basemap — see startCop).
const MAP_STYLE = "https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json";
// Center on the Strait of Hormuz — the demonstrator's theater (the hormuz multi-INT
// scenario lives at ~26.6N 56.6E). The resting screen must open ON the picture, not an
// empty ocean. (A view-fit-to-data pass is a later refinement; this is the 0.1b theater.)
const INITIAL_VIEW = { longitude: 56.6, latitude: 26.6, zoom: 8.0 };
// 2.5D-DEPTH: the default tactical tilt (degrees) for the 2.5D mode. 0 = flat plan-view
// (the default scope). 55 reads the altitude stack without the >85 experimental-pitch edge.
const TILT_PITCH_DEG = 55;

/** The current-picture high-water marking, derived from active entities. Drives
 *  the dual classification banner (system high-water + current-view releasability). */
export interface HighWater {
  classification: string;
  missions: string[];
  caveats: string[];
  releasability: string[];
}

/** The QUERY level — the SUBJECT side of the dual banner (the top row). This is the
 *  level you're working AT = min(your real clearance, the NPE you talk *through*, your
 *  voluntary down-filter). It is NOT the data ceiling (a data ceiling would leak that
 *  higher-classification content exists — violates Glomar). The down-filter dropdown
 *  drives `queryLevel`; the as-rendered `HighWater` (what's actually on screen at that
 *  level) is the SECOND banner row. (TODO §"dual banner"; modeled on TDFBot's IxD.) */
export interface QueryScope {
  /** The active working level (the down-filter selection); colors the top banner. */
  queryLevel: string;
  /** The persona's real maximum clearance — the dropdown can never exceed it. */
  clearanceLevel: string;
  /** The NPE (gateway / KAS / agent you talk *through*) cap; defaults to the
   *  clearance level in 0.1b (direct path, no reduction). Structure supports a real
   *  path cap later — the query level then folds it in as the min(). */
  npeLevel: string;
  /** Selectable levels low→high, each ≤ min(clearanceLevel, npeLevel). */
  available: string[];
  /** Subject full clearance marking at the current query level (level + SCI compartments +
   *  caveats from the NARROWED effective claims), shaped as a HighWater so BannerStack renders
   *  ROW 1 via bannerLine()/bannerTier(). Glomar-safe: the SUBJECT OWN descoped ceiling, not a
   *  data ceiling. When a view-as overlay narrows an axis, ROW 1 states the DESCOPED subject
   *  ceiling (e.g. SECRET · REL GBR) — never the real, wider clearance. */
  queryHighWater: HighWater;
  /** The active MULTI-DIM view-as overlay, or null when the operator is at their FULL view
   *  (no descope). The UI reads this to render the view-as chip / EXIT affordance. `spec` is the
   *  operator's requested narrowing (the raw DescopeSpec, pre-clamp); `label` is a short human
   *  summary ("SECRET · REL GBR", "as GBR"); `exact` is specDominatedByHeld — true iff the held
   *  clearance ⊇ every requested value so the preview reproduces the named target exactly, false
   *  iff the descope is STRICTER than named (an unheld value / a ceiling clamp). */
  viewAs: { spec: DescopeSpec; label: string; exact: boolean } | null;
  /** The operator's OWN held values per bundle axis (releasability, sci_control_system,
   *  sci_compartment, handling_caveat), in proper casing, straight from realClaims — NOT the
   *  narrowed view. The ViewAsPanel renders "keep only held" controls from this (a descope can
   *  only ever KEEP a held value), so the SCI drop controls are correct at every level rather
   *  than reconstructed from the level-gated queryHighWater. Glomar-safe: these are the SUBJECT's
   *  own claims, never data markings. */
  heldValues: Record<string, string[]>;
}

/** The TIME-WINDOW (recency) control state — the log-scale "show me the last N" filter
 *  (docs/AOA-STALENESS-TIME-WINDOW.md). A pane-of-glass recency filter that the projection
 *  applies as paint membership (clearance ∩ recency); the slider control renders this. */
export interface TimeWindowState {
  /** The active recency width in ms; `-1` sentinel means "all time" (no recency filter). */
  widthMs: number;
  /** Human label for the active width ("30 min", "2 hr", …, "all time"). */
  widthLabel: string;
  /** True when the anchor follows `now` (live); false when scrubbed to a pinned past time. */
  live: boolean;
  /** Index into the log-scale stop ladder (for the −/+ control to know its bounds). */
  stopIndex: number;
  /** How many stops the ladder has (so the control can disable −/+ at the ends). */
  stopCount: number;
}

/** The result of a REAL KAS rewrap on the selected protected contact.
 *  "denied" is ONLY a policy verdict (the ABAC decision said no). "unavailable" is a
 *  NO-DECISION outcome — an auth/transport failure UPSTREAM of the ABAC decision (expired
 *  session token, KAS offline/unreachable): content stays SEALED, but there is a clear
 *  recovery path (silent refresh / reconnect + retry), so it must never wear the DENY
 *  costume nor persist as one (Will, 2026-07-05). The server mirrors this taxonomy: an
 *  exp-rejected rewrap writes NO policy audit line. */
export interface DecryptState {
  state: "idle" | "rewrapping" | "granted" | "denied" | "unavailable";
  plaintext?: string;
  /** The decrypted inner marking (CONTRACT:P4-COT-TDF-WRAPPER) — can be HIGHER than the
   *  wire/outer floor; drives banner elevation + the inspector header while shown. */
  marking?: { classification: string; caveats?: string[]; releasability?: string[]; mission?: string[] };
  reason?: string;
}

/** Live status the HUD subscribes to. Updated off the render loop. */
export interface CopStatus {
  // --- existing 6 (the built HUD/banner/inspector seam) ---
  feedMode: FeedMode;
  trackCount: number;
  alertCount: number;
  selected: CanonicalEnvelope | null;
  highWater: HighWater;
  decrypt: DecryptState;

  /** KAS reachability (Phase 2 Step E). True when the KAS heartbeat has declared the KAS
   *  UNREACHABLE (N consecutive failed probes). While true the COP fails closed: protected
   *  content stays sealed at its OUTER marking (decryptTdf skips the rewrap) and the BannerStack
   *  shows "KAS OFFLINE — decrypt unavailable". Clears on the first successful probe. */
  kasOffline: boolean;

  // --- 0.1b "Clean Shell" host-context contract (GROUP 0 seam).
  //     Spec: docs/ux/SHELL-COMPOSITION.md §3.1. Every aggregate below is derived
  //     from the CLEARED-VISIBLE set ONLY (pdp.visibleEnvelopes()) — Glomar-safe. ---

  /** Verdict glyph state — shape-primary face + ACP-125 urgency + what raised it.
   *  Derived from the VISIBLE entity set only (no hidden-object leak). [§2.2] */
  verdict: VerdictState;

  /** Living Spine: the active feeds as host-generated rows (visible-only counts). [§2.3] */
  spine: LayerRow[];

  /** The PDP-filtered Catalog universe for the +Add flip (role-grouped, search-first).
   *  Withheld leaves are simply ABSENT (Glomar). [§2.3] */
  catalog: CatalogGroup[];

  /** Census denominators over the CLEARED-VISIBLE set only. [§2.7] */
  census: CensusCounts;

  /** Edition/connectivity cartouche state. [§2.8] */
  edition: CartoucheState;
  /** Live cursor coord/MGRS (throttled emit; null when off-map). [§2.8] */
  cursor: CursorReadout | null;

  /** The QUERY level + the down-filter dropdown's selectable range (top banner). */
  queryScope: QueryScope;

  /** The TIME-WINDOW (recency) control state — the log-scale "last N" recency filter. */
  timeWindow: TimeWindowState;

  /** Right-gutter Ledger rest-state summary (visible-only). [§2.5] */
  pictureSummary: PictureSummary;
  /** The cited Understudy (AI) facet; null until available. [§2.5] */
  understudy: UnderstudyEntry | null;

  /** Throughline ranked results (fanned by runQuery). [§2.6] */
  queryResults: QueryResult[];
  /** The active in-place FILTER (fade-not-hide); null when none. [§2.6] */
  activeFilter: ActiveFilter | null;

  /** Layout recipe (for GutterFrame collapse + SettingsDrawer checkmarks). [§2.1] */
  layout: LayoutRecipe;
  /** Active render profile id (for SettingsDrawer checkmarks). [§2.9] */
  renderProfile: string;
  /** The active synthetic THEATER id (SCENARIO_REGISTRY key — hormuz/taiwan/levant/
   *  global-intel). Drives the layers-pane "Theaters" radio highlight. Only meaningful in
   *  mock mode (feedMode === "mock"); the scenarios ARE the mock backdrop. Boot value is
   *  the ?scenario= URL selection; handle.setScenario(id) swaps it live (no page reload). */
  activeScenario: string;
  /** Active symbology carrier — "milsym" (MIL-STD-2525 frame SHAPE), "dot" (legacy
   *  colour-fill marker) or "coalition" (full milsymbol MIL-STD-2525 symbols).
   *  Drives the SettingsDrawer Symbology active-mode radio. [§2.9] */
  markerMode: "milsym" | "dot" | "coalition";
  /** 2.5D-DEPTH: the active map view mode — "2d" (flat plan-view) or "2.5d" (tilted). */
  viewMode: "2d" | "2.5d";

  /** The append-only KAS/PDP verdict ledger (Tier B). The host OBSERVES each
   *  terminal access decision the spine made (it NEVER alters pdp/kas/decide/
   *  projection) and records it here; the LeftSlideout Audit pane renders it.
   *  Rule 4: verdicts live ONLY in this Audit pane + the Trust modal. Newest first,
   *  bounded to a ring of the most-recent rows. */
  audit: AuditRow[];

  /** The operator-facing ALERT LOG — the cleared-VISIBLE `alert`-kind entities the
   *  host painted, minus the ones the operator has CLEARED (a UI self-scope dismiss,
   *  NOT a security decision). Each row carries a `read` acknowledgement. Glomar-safe:
   *  a withheld alert is never in the engine's visible set, so it never enters this log
   *  (no hidden-alert denominator). Newest-first, bounded. */
  alertLog: AlertRow[];
  /** Global chat unread total — the Glomar-safe fold of chatApi.totalUnread(), pushed by
   *  the chat subscription. Drives the FunctionRail chat badge (a denied room can never
   *  surface a badge because totalUnread folds off the post-PDP-seam store). */
  chatUnread: number;

  /** TRUE-LOCATION sharing state (host/ownPosition.ts). True ONLY while the
   *  operator's own position is live on the picture (double-consented + fresh).
   *  Default OFF; drives the palette toggle title / any future rail affordance. */
  shareLocation: boolean;
}

const UNCLASS: HighWater = {
  classification: "UNCLASSIFIED",
  missions: [],
  caveats: [],
  releasability: [],
};

/** The canonical PRE-BOOT CopStatus default. Exported so the React shell (and any
 *  future consumer) can seed its `useState` from the SAME shape the host emits,
 *  instead of hand-maintaining a literal that drifts as the contract grows. This is
 *  the seam's single source of truth for "nothing has loaded yet". */
export const INITIAL_COP_STATUS: CopStatus = {
  feedMode: "connecting",
  trackCount: 0,
  alertCount: 0,
  selected: null,
  highWater: UNCLASS,
  decrypt: { state: "idle" },
  // Assume the KAS is reachable until the heartbeat's first probe says otherwise (decryptTdf
  // still fails closed per-call before then); the heartbeat flips this to true only after N
  // consecutive failed probes.
  kasOffline: false,
  verdict: { face: "quiet", urgency: "routine", reasons: [] },
  spine: [],
  catalog: [],
  census: { layers: 0, shown: 0, total: 0, hiddenByFilter: 0, released: 0, reduced: 0, requestable: 0 },
  edition: { name: "LIVE", fidelity: "full", linkTier: "connected" },
  cursor: null,
  queryScope: {
    queryLevel: "UNCLASSIFIED",
    clearanceLevel: "UNCLASSIFIED",
    npeLevel: "UNCLASSIFIED",
    available: ["UNCLASSIFIED"],
    queryHighWater: UNCLASS,
    viewAs: null,
    heldValues: {},
  },
  timeWindow: {
    widthMs: 30 * 60_000,
    widthLabel: "30 min",
    live: true,
    stopIndex: 2,
    stopCount: 8,
  },
  pictureSummary: {
    headline: "No contacts",
    rows: [],
    stats: [
      { tone: "released", label: "RELEASED TO YOU", value: 0 },
      { tone: "reduced", label: "REDUCED FIDELITY", value: 0 },
      { tone: "requestable", label: "REQUESTABLE", value: 0 },
      { tone: "hostile", label: "HOSTILE · KNOWN", value: 0 },
    ],
    requestable: 0,
  },
  understudy: null,
  queryResults: [],
  activeFilter: null,
  layout: "cockpit",
  // Boot applies COP_THEME (INTEL_SKIN) via applyTheme() in startCop — the initial
  // profile id MUST match it so the SettingsDrawer checkmark is honest at boot
  // (was "dark", which painted a false Night checkmark over the intel skin).
  renderProfile: "intel-skin",
  // Boot theater = the registry default (hormuz). startCop overrides this with the live
  // ?scenario= selection before the first emit so the radio highlight is honest at boot.
  activeScenario: DEFAULT_SCENARIO_ID,
  // Boot marker carrier == deckBackend's DEFAULT_MARKER_MODE ("milsym") so the
  // Symbology active-mode radio is honest before any toggle.
  markerMode: "milsym",
  viewMode: "2d",
  audit: [],
  alertLog: [],
  chatUnread: 0,
  // TRUE-LOCATION sharing is OPT-IN (rail 1) — always boots OFF, even if the
  // persisted presence.shareLocation preference is true (consent is per-session).
  shareLocation: false,
};
// defense.local hierarchy: TOP_SECRET > SECRET > CONFIDENTIAL > CUI > UNCLASSIFIED.
// CLASS_ORDER + LEVELS are imported from abac-pdp (the ONE shared ladder — M3 fix).
// LEVELS aliases CLASS_LEVELS: the down-filter dropdown offers a prefix of this list
// (≤ the clearance/NPE cap), and LEVELS[CLASS_ORDER[x]] === x.
const LEVELS = CLASS_LEVELS;
/** The persona's max clearance from the JWT classification claim (the hierarchy
 *  attribute the local PDP reads via userValuesFor). The LIVE defense.local model
 *  keys it `classification`; the legacy test fixtures use `classification_level` —
 *  accept either. Fail-closed to UNCLASSIFIED. */
const clearanceLevelOf = (c: SubjectClaims): string => {
  const rec = c as Record<string, string | string[]>;
  const raw = rec["classification"] ?? rec["classification_level"];
  // Live JWT claims are LOWERCASE (e.g. "top_secret"); the PDP lc's both sides.
  // Normalize to the uppercase canonical so the CLASS_ORDER lookup matches.
  const s = (Array.isArray(raw) ? raw[0] : raw)?.toUpperCase?.();
  return typeof s === "string" && s in CLASS_ORDER ? s : "UNCLASSIFIED";
};

// The evaluator-identical value projection (lc, array): mirrors userValuesFor / narrowHeld so a
// per-axis SET comparison folds case exactly as the PDP would. Used ONLY to compare two claim
// maps (never to pre-clean claims fed to the seam).
const lcClaimValues = (v: string | string[] | undefined): string[] =>
  Array.isArray(v) ? v.map((x) => String(x).toLowerCase()) : v != null && v !== "" ? [String(v).toLowerCase()] : [];
// The COP's legacy duplicate of the classification claim — folded by the hierarchy check below,
// never treated as a SET axis (a set-subset test on it would double-count the level drop).
const NARROW_SKIP_KEYS = new Set(["classification", "classification_level"]);

/** True iff `next` is a strict NARROWING of `prev` on ANY axis: the classification ceiling
 *  dropped (hierarchy order), OR a set axis (releasability / SCI / caveat / …) lost a value it
 *  held. A pure WIDEN (next gains values / a higher ceiling) is NOT a narrowing (the copilot
 *  thread survives — you are cleared for more, not less). Case-folded per the evaluator's own
 *  projection so casing never masks a real narrowing. */
const isViewNarrowing = (prev: SubjectClaims, next: SubjectClaims): boolean => {
  const pRec = prev as Record<string, string | string[] | undefined>;
  const nRec = next as Record<string, string | string[] | undefined>;
  // Classification: a strict hierarchy-order DROP is a narrowing (disjoint level strings mean a
  // set-subset test would miss it — compare the ladder order instead).
  const pc = CLASS_ORDER[clearanceLevelOf(prev)] ?? 0;
  const nc = CLASS_ORDER[clearanceLevelOf(next)] ?? 0;
  if (nc < pc) return true;
  // Every other axis: losing a held value (present in prev, absent from next) is a narrowing.
  for (const axis of new Set([...Object.keys(pRec), ...Object.keys(nRec)])) {
    if (NARROW_SKIP_KEYS.has(axis)) continue;
    const nSet = new Set(lcClaimValues(nRec[axis]));
    for (const v of lcClaimValues(pRec[axis])) if (!nSet.has(v)) return true;
  }
  return false;
};

/** A short human summary of a view-as overlay for ROW-1 chips ("SECRET · REL GBR", "as GBR").
 *  Classification first (proper case), then releasability ("REL …"), then any remaining named
 *  axes as "KEY val/val". Empty overlay ⇒ "" (no chip). */
const viewOverlayLabel = (spec: DescopeSpec): string => {
  const parts: string[] = [];
  const cls = spec["classification"];
  if (cls != null) {
    const v = Array.isArray(cls) ? cls[0] : cls;
    if (v) parts.push(String(v).toUpperCase());
  }
  const rel = spec["releasability"];
  if (rel != null) {
    const vals = (Array.isArray(rel) ? rel : [rel]).filter(Boolean).map((x) => String(x).toUpperCase());
    if (vals.length) parts.push(`REL ${vals.join("/")}`);
  }
  for (const [k, raw] of Object.entries(spec)) {
    if (k === "classification" || k === "classification_level" || k === "releasability") continue;
    const vals = (Array.isArray(raw) ? raw : [raw]).filter(Boolean).map((x) => String(x));
    if (vals.length) parts.push(`${k.toUpperCase()} ${vals.join("/")}`);
  }
  return parts.join(" · ");
};

export interface CopHandle {
  /** Tear everything down (engine, host, feed, map). */
  dispose(): Promise<void>;
  /** Subscribe to coarse status changes for the HUD. */
  onStatus(cb: (s: CopStatus) => void): () => void;
  /** Subscribe to GraphView summon requests (openGraph fires this with the entity
   *  id; null = dismissed). The React shell renders the GraphView overlay off this
   *  seam — React never reaches the engine. Mirrors onStatus. [§2.5] */
  onGraph(cb: (id: string | null) => void): () => void;
  /** NARROW panel-mount accessor (Seam B, CONTRACT:C3-CAPABILITY-PLUGIN.1.0). Every
   *  registered `PanelSpec` across all capabilities, paired with its owning capability
   *  id — what `<PanelHost>` renders generically (each spec's `mount` puts the panel's
   *  own content on screen). This exposes ONLY the panel list off the host, NOT the host
   *  itself (the three-planes rule — App.tsx never reaches a capability or the engine). */
  panels(): PanelContribution[];
  /** CHANGE SIGNAL for `panels()`. Capability activation is async, so a one-shot read
   *  after boot misses panels registered later; this fires whenever the panel set may
   *  have changed (each activation). Mirrors `onGraph` — React re-reads `panels()` on the
   *  bump. Returns an unsubscribe. The cb is also invoked ONCE on subscribe with the
   *  current set so a late subscriber never misses the boot-time panels. */
  onPanelsChanged(cb: () => void): () => void;
  /** Switch the logged-in identity (the JWT claims + token) and RE-FILTER the whole
   *  picture for it (the "force-down what I can see" control, real-identity-driven).
   *  The token becomes the new rewrap subject — one identity drives both I4 and I5. */
  setIdentity(claims: SubjectClaims, token: string): void;
  /** Clear the current selection. */
  clearSelection(): void;
  /** Re-run the KAS rewrap for the CURRENT selection iff the last attempt ended with NO
   *  decision (state "unavailable" — auth/transport upstream of the ABAC engine). A GRANT
   *  or a policy DENY is a real verdict and is never re-run here (deselect→reselect
   *  re-decides, as before). Wired to Trust-modal open: an expired-token failure has a
   *  clear recovery path (silent refresh + retry) and must not persist as a denial. */
  retryDecrypt(): void;
  /** Recenter the map on the theater. */
  recenter(): void;
  /** Switch the affiliation carrier at runtime: MIL-STD-2525 frame-shapes ("milsym",
   *  the default) ⇄ legacy fill-coloured dots ("dot") ⇄ full milsymbol 2525 symbols
   *  ("coalition", lazy-loaded). The deck rebuilds in place. */
  setMarkerMode(mode: "milsym" | "dot" | "coalition"): void;
  /** Switch the render profile (theme) at runtime by THEMES id (e.g. "dark",
   *  "mono-nvg", "high-contrast", "intel-skin"): reskins :root and re-points the
   *  affiliation palette so day/night/NVG profiles recolour everything in place. */
  setRenderProfile(themeId: string): void;
  /** 2.5D-DEPTH: flip the camera between the flat plan-view scope ("2d", the default) and
   *  the tilted 2.5D mode ("2.5d") that lifts air/UAS tracks to altitude. Pure camera
   *  concern (pitch only) — animated on the map; the data view (center/zoom) is untouched. */
  setViewMode(mode: "2d" | "2.5d"): void;

  /** LIVE THEATER SWITCH (mock only — TODO#scenario-switch-ui). Stop the current synthetic
   *  theater, CLEAR the old theater's entities from the engine + PDP + projection (the host's
   *  sole-writer remove path, then a lockstep wipe of the derived-state stores so no stale
   *  track can resurrect on a later re-filter), then start the new theater on the SAME bus →
   *  PDP → projection → engine path (clearance-safe by construction — no bypass), recenter on
   *  it, and update status.activeScenario. No-op outside mock mode / for an unknown or
   *  already-active id. Guards against double-start (a single scenario runs at any time). */
  setScenario(id: string): void;
  /** The selectable theaters as `{ id, label }` rows (SCENARIO_REGISTRY order) — the small
   *  accessor the layers pane renders to list theaters + highlight status.activeScenario. */
  scenarios(): Array<{ id: string; label: string }>;

  // --- 0.1b "Clean Shell" host-context contract (GROUP 0 seam).
  //     Spec: docs/ux/SHELL-COMPOSITION.md §3.2. Each mirrors the existing
  //     recenter/clearSelection imperative pattern (React never reaches the engine). ---

  /** COLLAPSE CONTRACT: re-fit the map's drawing buffer to the new container box
   *  after a gutter reflow, holding the camera fixed so tracks DON'T slide under
   *  glass. Production analog of graybox resizeDeck — calls map.resize() (MapLibre
   *  re-fits its WebGL2 canvas; the deck MapboxOverlay rides along) with viewState
   *  held. Idempotent; call on every reflow tick. [§4] */
  refitMap(): void;

  // --- URL STATE CAPTURE (shareable "look at this one" deep links; host/urlState.ts).
  //     Presentation/navigation ONLY (I13) — these expose the LIVE camera + a jump-to
  //     restore + a camera-settle hook so the URL fragment can reflect/restore the view.
  //     They never gate visibility; the restore SELECT still runs the full PDP/KAS path. ---

  /** Read the LIVE camera (center + zoom) for reflecting the view into the URL hash.
   *  Returns null headless / before the map is ready. */
  getCamera(): { lat: number; lng: number; zoom: number } | null;
  /** JUMP (no animation) the camera to a restored view on load — engine.setView →
   *  MapLibre jumpTo. Presentation only; the data view is untouched (nothing is
   *  re-gated). Used by the URL-state controller's initial-load restore. */
  setCamera(cam: { lat: number; lng: number; zoom: number }): void;
  /** Subscribe to camera-settle (MapLibre "moveend") so the URL reflects the view once
   *  a pan/zoom settles. Returns an unsubscribe. */
  onMoveEnd(cb: () => void): () => void;

  /** Throughline: run one TypedQuery AST → fan results to status.queryResults. [§2.6] */
  runQuery(ast: unknown): void;
  /** GO rung: fly + halo an entity. [§2.6] */
  locate(id: string): void;
  /** SELECT (from Verdict drill / spine row / result row): drive the Ledger re-skin. [§2.5] */
  select(id: string): void;
  /** FILTER rung: re-scope in place (fade-not-hide); updates status.activeFilter + census. [§2.6] */
  applyFilter(filterAst: unknown): void;
  /** DO rung: stage an action card (no commit). [§2.6] */
  stageAction(card: unknown): void;

  /** Living Spine row controls. [§2.3] */
  setLayerVisible(id: string, visible: boolean): void;
  setLayerOpacity(id: string, opacity: number): void;
  zoomToLayer(id: string): void;
  removeLayer(id: string): void;
  /** +Add Catalog leaf → render it, add a spine row. [§2.3] */
  addLayer(catalogLeafId: string): void;

  /** Open the relationship graph (GraphView) for an entity, from the Ledger. [§2.5] */
  openGraph(id: string): void;

  /** ALERT LOG controls (OPERATE). `markAlertRead` acknowledges ONE alert (toggles its
   *  `read` state — a UI acknowledgement, NOT a security decision). `dismissAlert`
   *  dismisses ONE alert from the operator's log (the per-row ×); `clearAllAlerts`
   *  dismisses EVERY currently-visible alert. A dismiss also quiets the VerdictGlyph
   *  (the verdict counts only un-dismissed alerts) but never removes the underlying
   *  objects from the registry/map; a re-emit re-surfaces the alert (unread). All three
   *  operate ONLY over the cleared-visible alert set (Glomar), and the read/cleared state
   *  is persisted PER USER via the I8 settings store (display state, survives reload). */
  markAlertRead(id: string): void;
  dismissAlert(id: string): void;
  clearAllAlerts(): void;

  /** TRUE-LOCATION sharing toggle (host/ownPosition.ts). `on=true` runs the
   *  DOUBLE-CONSENT flow (our dialog → the browser geolocation prompt) and
   *  resolves the resulting sharing state; `on=false` stops the watch AND the
   *  emission immediately (fail-closed — nothing publishes after OFF). The
   *  palette folds this as "Share my location ON/OFF". */
  setShareLocation(on: boolean): Promise<boolean>;

  /** CALL FOR EVAC (host/evacRequest.ts): open the composer (urgency + freetext
   *  over the resolved location — own position if sharing is ON, else the
   *  selected point / map center, STATED in the request), then publish the
   *  kind:"alert" subtype:"evac" envelope through the bus AND the S1 /emit
   *  egress when armed. Resolves after send/cancel. */
  callForEvac(): Promise<void>;

  /** Device-class layout recipe switch (static recompose; calls refitMap after). [§2.9] */
  setLayout(layout: LayoutRecipe): void;

  /** Down-filter: voluntarily VIEW the picture at a lower level (≤ your real
   *  clearance, capped by the NPE). Thin wrapper over the multi-dim view-as overlay: it writes
   *  spec.classification = [level] into the overlay and re-applies. Re-resolves the whole picture
   *  to that query level — purely subtractive (Glomar-safe; it can only hide MORE). Drives the
   *  top banner color + text. [§"dual banner / down-filter"] */
  setQueryLevel(level: string): void;

  /** MULTI-DIM VIEW-AS: voluntarily descope the picture on ANY combination of axes (classification
   *  ceiling, releasability, SCI, caveat) — the generalization of setQueryLevel. `spec` MERGES into
   *  the active overlay (a partial spec narrows only the named axes; omitted axes keep their current
   *  overlay value); pass `null` to CLEAR the overlay entirely (EXIT view-as ⇒ full view). Purely
   *  subtractive over the real clearance (Glomar-safe; down-only — unheld/omitted values contribute
   *  nothing). Re-resolves the whole picture and updates status.queryScope.viewAs. */
  setViewOverlay(spec: DescopeSpec | null): void;

  /** TIME-WINDOW recency control. Step the log-scale width −1/+1 (the two-button slider);
   *  marks the width operator-owned (auto-widen never fires again). Purely subtractive at
   *  the pane of glass — never fed to the feed/source. */
  stepTimeWindow(dir: number): void;
  /** Set the recency width by ladder index directly (0 = narrowest … N-1 = all time). */
  setTimeWindowIndex(index: number): void;
  /** Return the recency anchor to LIVE (follow `now`) after a scrub. */
  goLive(): void;

  /** The COP Copilot capability's panel-facing API (CONTRACT:C3-CAPABILITY-PLUGIN.1.0).
   *  The host owns the capability lifecycle (register/activate); React mounts
   *  <CopilotPanel api={handle.copilotApi} /> off this seam — it never reaches the
   *  capability or the engine. The copilot reasons ONLY over the host-filtered
   *  (PDP-visible) working set, so a denied object never reaches the panel (Glomar). */
  copilotApi: CopilotApi;

  /** The encrypted-chat capability's panel-facing API (CONTRACT:C3-CAPABILITY-PLUGIN.1.0,
   *  @tak-tdf/cap-chat). The host owns the capability lifecycle (register/activate);
   *  Wave-3 React mounts <ChatPanel api={handle.chatApi} /> off this seam — it never
   *  reaches the capability or the engine. The body is E2E-sealed (I5 chatKeyAccess); the
   *  room list folds STRICTLY off post-PDP-seam envelopes, so a denied room is absent
   *  (Glomar). The store is REBUILT on every persona switch (R2 — a HIGH body vanishes on
   *  a switch-to-LOW). */
  chatApi: ChatApi;
}

// The capability-seam policy (CONTRACT:I4-POLICY-DECISION.1.0) is built INLINE at the
// CapabilityHost construction below, backed by the live PDP (pdp.bundle()/currentClaims())
// via seamPolicy.decideForResource — so an extension/AI is gated by the SAME entitlements
// that filter the rendered picture. The old permit-all demo policy is gone (ADR-0008 C1).

/** If an envelope is backed by a REAL KAS TDF (a same-origin .tdf URL seeded by
 *  scripts/seed-cop-tdf.mjs, or an inline data:tdf from the gateway), return its
 *  ref; otherwise null. Mock "encrypted" flags are cosmetic and are NOT
 *  rewrappable — only real_kas tracks hit the live KAS (client-side, via host/kas.ts). */
function realKasRef(env: CanonicalEnvelope): string | null {
  const real = (env.properties as Record<string, unknown>)["real_kas"] === true;
  const ref = (env.payload as { encrypted_ref?: string } | undefined)?.encrypted_ref;
  return real && typeof ref === "string" ? ref : null;
}

/** Compact a policy ref for the Audit ledger meta line ("policy://defense.local" →
 *  "defense.local"). Honest fallback when an envelope carries no policy ref. Pure. */
function compactPolicyRef(ref: string | undefined): string {
  if (typeof ref !== "string" || ref.length === 0) return "policy://default";
  return ref.replace(/^policy:\/\//, "");
}

/** Build a CanonicalEnvelope for a seeded, REAL-KAS-backed protected contact. Its
 *  markings drive the local PDP (I4 filter); its .tdf URL drives the client-side KAS
 *  open (I5 release) — one object, one model. */
function realKasTrack(o: SeededObject): CanonicalEnvelope {
  const now = new Date().toISOString();
  return {
    id: o.id,
    schema: "scop.envelope/1.0",
    kind: "track",
    time: now,
    valid_time: { start: now, stale: new Date(Date.now() + 3_600_000).toISOString() },
    geometry: { type: "Point", coordinates: o.coords },
    properties: { name: o.name, affiliation: o.affiliation as Affiliation, type: "intel-isr", domain: "uas", source: "real-kas", real_kas: true },
    security: {
      classification: o.security.classification,
      releasability: o.security.releasability,
      mission: o.security.mission,
      caveats: o.security.caveats,
      originator_control: o.security.caveats.includes("ORCON"),
      policy_ref: "policy://defense.local",
      payload_mode: "encrypted",
      leakage_profile: "clear-shell",
    },
    provenance: {
      originator: "cop-seed",
      adapter: "real-kas",
      received_at: now,
      chain: [{ adapter: "real-kas", at: now }],
    },
    payload: { encrypted_ref: o.tdf },
  } as CanonicalEnvelope;
}

/** Group key for a Living-Spine row: the feed source if present, else the kind.
 *  Pure + visible-only-safe (callers pass already-cleared envelopes). */
function spineKeyFor(env: CanonicalEnvelope): string {
  const src = env.properties.source;
  return typeof src === "string" && src.length > 0 ? `src:${src}` : `kind:${env.kind}`;
}

/** Human label for a Living-Spine row derived from its group key. */
function spineLabelFor(env: CanonicalEnvelope): string {
  const src = env.properties.source;
  if (typeof src === "string" && src.length > 0) return src;
  return env.kind;
}

// NOTE: the former module-level `isStale()` was retired in IVM Increment 2 — the spine health
// glyph now reads the per-group MIN finite stale timestamp (spineHist.staleMin) vs Date.now() at
// recompute-snapshot time, so a per-object isStale fold is no longer needed. The finite-stale
// parse lives in the closure's `finiteStaleTsOf` (byte-identical to the old parse).

/** Role group for a spine/catalog key, derived from the envelope kind/source. Pure;
 *  used to build the role-grouped Catalog (§2.3) over the visible set only. */
function roleGroupFor(env: CanonicalEnvelope): string {
  switch (env.kind) {
    case "alert":
      return "Alerts";
    case "observation":
      return "Observations";
    case "track":
      break;
    default:
      return "Other";
  }
  const aff = env.properties.affiliation;
  if (aff === "hostile") return "Hostile tracks";
  if (aff === "friend") return "Friendly tracks";
  return "Tracks";
}

/** A compact MGRS-like grid reference from a lon/lat. This is the standard
 *  MGRS construction (UTM zone + latitude band + 100km square + truncated
 *  easting/northing) at 1m precision — an honest, self-contained readout for the
 *  Cartouche (no network, no heavy geodesy dep). Defensive: returns "—" off-grid. */
function toMgrs(lon: number, lat: number): string {
  if (!Number.isFinite(lon) || !Number.isFinite(lat) || lat < -80 || lat >= 84) return "—";
  const a = 6378137.0;
  const f = 1 / 298.257223563;
  const e2 = f * (2 - f);
  const ep2 = e2 / (1 - e2);
  const zone = Math.floor((lon + 180) / 6) + 1;
  const lonOrigin = ((zone - 1) * 6 - 180 + 3) * (Math.PI / 180);
  const latR = lat * (Math.PI / 180);
  const lonR = lon * (Math.PI / 180);
  const N = a / Math.sqrt(1 - e2 * Math.sin(latR) ** 2);
  const T = Math.tan(latR) ** 2;
  const C = ep2 * Math.cos(latR) ** 2;
  const A = Math.cos(latR) * (lonR - lonOrigin);
  const M =
    a *
    ((1 - e2 / 4 - (3 * e2 ** 2) / 64 - (5 * e2 ** 3) / 256) * latR -
      ((3 * e2) / 8 + (3 * e2 ** 2) / 32 + (45 * e2 ** 3) / 1024) * Math.sin(2 * latR) +
      ((15 * e2 ** 2) / 256 + (45 * e2 ** 3) / 1024) * Math.sin(4 * latR) -
      ((35 * e2 ** 3) / 3072) * Math.sin(6 * latR));
  const k0 = 0.9996;
  const easting =
    k0 * N * (A + ((1 - T + C) * A ** 3) / 6 + ((5 - 18 * T + T ** 2 + 72 * C - 58 * ep2) * A ** 5) / 120) +
    500000.0;
  let northing =
    k0 *
    (M +
      N *
        Math.tan(latR) *
        (A ** 2 / 2 +
          ((5 - T + 9 * C + 4 * C ** 2) * A ** 4) / 24 +
          ((61 - 58 * T + T ** 2 + 600 * C - 330 * ep2) * A ** 6) / 720));
  if (lat < 0) northing += 10000000.0;
  const bands = "CDEFGHJKLMNPQRSTUVWX";
  const band = bands.charAt(Math.floor((lat + 80) / 8));
  // 100km square id (column letters cycle per zone set, rows per zone parity).
  const colSet = "ABCDEFGH"; // set 1 letters; reduced table — good enough for the readout
  const colLetters = ["ABCDEFGH", "JKLMNPQR", "STUVWXYZ"];
  const col = colLetters[(zone - 1) % 3].charAt((Math.floor(easting / 100000) - 1) % 8);
  const rowLetters = "ABCDEFGHJKLMNPQRSTUV";
  const rowBase = (zone - 1) % 2 === 0 ? 0 : 5;
  const row = rowLetters.charAt((Math.floor(northing / 100000) + rowBase) % 20);
  void colSet;
  const e5 = String(Math.floor(easting % 100000)).padStart(5, "0");
  const n5 = String(Math.floor(northing % 100000)).padStart(5, "0");
  return `${zone}${band} ${col}${row} ${e5} ${n5}`;
}

/** Boot the COP onto a map container for a logged-in identity. Returns once the
 *  map style has loaded. */
export async function startCop(mapContainer: HTMLElement, identity: CopIdentity): Promise<CopHandle> {
  // --- Plane 3: theme → :root + affiliation palette ---
  const theme = applyTheme();
  const affiliationColors = affiliationColorsFor(theme);

  // --- The map (owns the WebGL2 surface + camera) ---
  const map = new maplibregl.Map({
    container: mapContainer,
    style: MAP_STYLE,
    center: [INITIAL_VIEW.longitude, INITIAL_VIEW.latitude],
    zoom: INITIAL_VIEW.zoom,
    // 2.5D-DEPTH: allow camera tilt (default MapLibre maxPitch is 60). 85 is the v5 hard
    // ceiling; the 2.5D mode tilts to TILT_PITCH_DEG (55). Boot stays flat (pitch 0).
    maxPitch: 85,
    // 2.5D-DEPTH: Shift+drag is repurposed for Google-Earth tilt/orbit (below), not box-zoom.
    boxZoom: false,
    attributionControl: { compact: true },
  });
  // visualizePitch: the compass reads/resets the tilt so the operator owns the camera.
  map.addControl(
    new maplibregl.NavigationControl({ showCompass: true, visualizePitch: true }),
    "top-right",
  );

  // Track the active basemap URL so a render-profile swap only setStyle()s when it actually
  // changes (setStyle reloads the whole style + refetches tiles). Starts on the dark basemap.
  let activeBasemapUrl = MAP_STYLE;

  // COLD-LOAD: the WebGL canvas can mount before the shell grid settles its final size,
  // leaving a momentarily-black scope until the first user interaction triggers a resize.
  // Nudge resize()+triggerRepaint() across the first frames (the scheduleRefit cadence) so
  // the initial paint fills the container without needing a gesture. Guarded for the
  // StrictMode double-mount (the map may be torn down between nudges).
  for (const t of [0, 32, 80, 160]) {
    setTimeout(() => {
      try {
        map.resize();
        map.triggerRepaint();
      } catch {
        /* map torn down on a StrictMode remount — ignore */
      }
    }, t);
  }

  // 2.5D-DEPTH P4: 3D terrain from a raster-dem (terrarium-encoded) source. OFFLINE-FIRST — the
  // DEM tiles are self-hosted under /tiles/dem (public-domain USGS/NASA SRTM+GMTED2010); set
  // VITE_OFFLINE_TILES=false to fall back to the AWS open-data CDN. setTerrain needs the style
  // loaded (hence map.on("load")), is ZOOM-GATED (>=9) so the resting wide picture pays no terrain
  // cost, and is purely additive — if "load" never fires (StrictMode remount) the COP still boots.
  const OFFLINE_TILES = import.meta.env.VITE_OFFLINE_TILES !== "false";
  const DEM_TILES = OFFLINE_TILES
    ? ["/tiles/dem/{z}/{x}/{y}.png"]
    : ["https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png"];
  // exaggeration 1.0: deck.gl objects render at TRUE metres and do NOT inherit terrain exaggeration,
  // so any >1 here desyncs lifted tracks/stalks from the (amplified) terrain mesh — a low track could
  // even appear below an exaggerated hill it's actually above. Keep 1:1 so altitude reads honestly.
  const TERRAIN = { source: "terrain-dem", exaggeration: 1.0 };
  const TERRAIN_MIN_ZOOM = 9;
  // Add the DEM source if absent (idempotent). MUST be re-run after a basemap setStyle(), which
  // drops every source in the outgoing style (see setRenderProfile's day/night basemap swap).
  const ensureTerrainSource = (): void => {
    if (!map.getSource("terrain-dem")) {
      map.addSource("terrain-dem", {
        type: "raster-dem",
        tiles: DEM_TILES,
        tileSize: 256,
        encoding: "terrarium", // MUST be terrarium for the AWS/self-hosted tiles (default is mapbox)
        maxzoom: 11, // matches the bundled DEM; MapLibre overzooms above with no 404 storm
        attribution: "USGS / NASA SRTM · GMTED2010 (public domain)",
      });
    }
  };
  const applyTerrain = (): void => {
    const want = map.getZoom() >= TERRAIN_MIN_ZOOM;
    const has = !!map.getTerrain();
    if (want && !has) map.setTerrain(TERRAIN);
    else if (!want && has) map.setTerrain(null); // free the mesh when zoomed out
  };
  // Re-establish terrain for the CURRENT style: idempotent source + zoom-gated mesh. Called on the
  // initial "load" AND after each basemap swap (setStyle fires "style.load", not "load").
  const installTerrainForStyle = (): void => {
    ensureTerrainSource();
    applyTerrain();
  };
  map.on("load", () => {
    installTerrainForStyle();
    map.on("zoom", applyTerrain);
    // Kick a repaint so the vector basemap computes tile coverage + requests its first tiles
    // even before any user interaction (avoids a momentarily-black scope on a cold load).
    map.triggerRepaint();
  });

  // Do NOT block boot on the map "load" event. Under React StrictMode the
  // mount effect runs twice; a second MapLibre map created in the same container
  // can fail to ever fire "load", which would hang the entire COP (no feed, no
  // tracks). The deck.gl overlay, the engine, and the feed do not need the
  // basemap loaded — the overlay attaches via addControl and entities render
  // once the map paints. So proceed immediately and let rendering catch up.

  // --- Status fan-out (off the render loop) ---
  // Seed from the canonical seam default. The visible-only derivations below
  // (recomputeShell) replace verdict/census/spine/pictureSummary on every emit;
  // GROUP-B-dependent fields stay at these sensible empties until their real
  // wiring lands (see the TRACKED-TASK markers in the handle methods).
  const status: CopStatus = { ...INITIAL_COP_STATUS, audit: [] };
  const statusSubs = new Set<(s: CopStatus) => void>();
  const emitStatus = (): void => {
    const snapshot = { ...status };
    for (const cb of statusSubs) cb(snapshot);
  };

  // --- THE APPEND-ONLY KAS/PDP AUDIT LEDGER (Tier B). A BOUNDED, OBSERVE-ONLY ring
  //     of terminal access verdicts: as each KAS rewrap resolves (grant/deny), the
  //     host RECORDS the verdict the spine already made — it NEVER alters pdp/kas/
  //     decide/projection (this is a pure side-channel observer). Rule 4: these
  //     verdicts surface ONLY in the LeftSlideout Audit pane + the Trust modal, never
  //     as ambient chrome. Newest-first; capped so a long session can't grow unbounded.
  //     The Zulu time label is the standard ACP-127 HHMMZ form (UTC), matching the mock.
  const AUDIT_CAP = 100;
  const zulu = (d: Date): string =>
    `${String(d.getUTCHours()).padStart(2, "0")}${String(d.getUTCMinutes()).padStart(2, "0")}Z`;
  // The actor short code for the audit ledger = the LIVE persona's compact tag. Seeded
  // from the boot label; re-derived on setIdentity from the new claims (a username-ish
  // claim, fail-soft to OPERATOR). Display only — NOT a security boundary (the spine's
  // pdp/kas gate the real decisions; this is the legible "who" on an OBSERVED verdict).
  // The raw username/sub from the JWT claims (the security subject) — fail-soft to the boot label.
  const usernameFromClaims = (c: SubjectClaims, fallback: string): string => {
    const rec = c as Record<string, unknown>;
    const raw = rec["preferred_username"] ?? rec["username"] ?? rec["sub"] ?? rec["name"];
    const s = Array.isArray(raw) ? raw[0] : raw;
    return typeof s === "string" && s.length > 0 ? s : fallback;
  };
  const shortCodeFromClaims = (c: SubjectClaims, fallback: string): string => {
    // The audit ledger actor = the persona's REAL NAME when known ("Sarah Chen"), upper-cased;
    // else the raw username. Display-only (NOT a security boundary): pdp/kas gate the real decision.
    const u = usernameFromClaims(c, "");
    if (!u) return fallback;
    return displayNameFor(u).toUpperCase();
  };
  // The chat AUTHOR/PRESENCE id = the live persona's REAL display NAME when known (NOT a
  // security boundary — the JWT via setKasToken is the real I5 rewrap subject). Fail-soft
  // to the raw username, then the boot label.
  const subjectIdFromClaims = (c: SubjectClaims, fallback: string): string => {
    const u = usernameFromClaims(c, "");
    if (!u) return fallback;
    return displayNameFor(u);
  };
  // Boot the audit actor from the real persona name when the boot claims name a known
  // persona; fail-soft to the boot label. Re-derived on every setIdentity below.
  let auditActor = shortCodeFromClaims(identity.claims, (identity.label || "OPERATOR").toUpperCase());
  /** OBSERVE a terminal KAS/PDP verdict → prepend a bounded audit row + emit. Pure
   *  side-channel: it reads decisions, it never makes or changes them. `viaCopilot`
   *  tags the actor "COPILOT/<persona>" when the access was copilot-initiated (no real
   *  copilot access path fires today — the flag is the honest forward-looking seam). */
  const recordAudit = (
    verdict: AuditVerdict,
    subject: string,
    policyRef: string,
    viaCopilot = false,
  ): void => {
    const actor = viaCopilot ? `COPILOT/${auditActor}` : auditActor;
    const row: AuditRow = { verdict, subject, actor, policyRef, time: zulu(new Date()) };
    status.audit = [row, ...status.audit].slice(0, AUDIT_CAP);
    emitStatus();
  };

  // --- Living-Spine LAYER OVERRIDES (B1). The spine groups visible envelopes by
  //     source/kind (spineKeyFor); these per-group overrides carry the operator's
  //     real layer ops (hide / opacity) so a toggle drives the engine gate AND
  //     persists across re-derivations. A REMOVED group is hidden + flagged so its
  //     members stay off the engine even as new members arrive on the feed.
  //     Glomar-safe: overrides only ever key off already-visible groups. ---
  const layerHidden = new Set<string>();
  const layerOpacity = new Map<string, number>();
  const layerRemoved = new Set<string>();

  // --- The active in-place FILTER predicate (B1, fade-not-hide). When set, a
  //     visible envelope that does NOT match is counted in census.hiddenByFilter
  //     and dropped from the engine (it stays in the PDP registry → reversible,
  //     and never leaks a hidden object: the denominator is the cleared-visible
  //     set only). null ⇒ no active filter. ---
  let filterPredicate: ((env: CanonicalEnvelope) => boolean) | null = null;

  // --- openGraph fan-out (B1/B2). The Ledger calls handle.openGraph(id); the React
  //     shell subscribes here to summon the GraphView overlay for that entity. Mirrors
  //     the onStatus seam — React never reaches the engine; the host fans the request. ---
  const graphSubs = new Set<(id: string | null) => void>();
  const emitGraph = (id: string | null): void => {
    for (const cb of graphSubs) cb(id);
  };

  // --- panel-mount change fan-out (Seam B). A capability's panel contributions are
  //     read back via host.panels(); activation is async, so a one-shot read after boot
  //     misses panels registered later. This signal lets the React shell re-read
  //     host.panels() whenever the set may have changed (each activation). Mirrors the
  //     onGraph/onStatus seam — React never reaches a capability or the engine. ---
  const panelSubs = new Set<() => void>();
  const emitPanelsChanged = (): void => {
    for (const cb of panelSubs) cb();
  };

  // --- Plane 1: render-engine + deck.gl WebGL2 backend ---
  // Selecting a REAL-KAS-backed contact triggers a live KAS rewrap to decrypt
  // its payload in-place (granted) or confirm it stays sealed (denied).
  // TRANSIENT (no-decision) rewrap reasons — auth/transport failures UPSTREAM of the ABAC
  // decision (I5 reason codes from kas.ts classifyKasError). These never persist as a DENY
  // on the selection and never write an audit verdict — the server writes NO policy audit
  // for them either (the rejection happens before the decision engine runs).
  const TRANSIENT_REWRAP_REASONS = new Set(["UNAUTHENTICATED", "KAS_OFFLINE", "KAS_UNAVAILABLE"]);
  const runDecrypt = (env: CanonicalEnvelope, ref: string): void => {
    status.decrypt = { state: "rewrapping" };
    emitStatus();
    void decryptTdf(ref).then((res) => {
      if (status.selected?.id !== env.id) return; // selection moved on
      const transient = !res.granted && TRANSIENT_REWRAP_REASONS.has(res.reason ?? "");
      status.decrypt = res.granted
        ? { state: "granted", plaintext: res.plaintext, marking: res.marking }
        : transient
          ? { state: "unavailable", reason: res.reason }
          : { state: "denied", reason: res.reason };
      emitStatus();
      // The decrypted inner marking (P4) can EXCEED the wire/outer floor — re-derive the
      // banner so "highest classification on screen" reflects the detail now rendered.
      recomputeBanner();
      const name = String(env.properties.name ?? env.id);
      // OBSERVE the terminal KAS verdict into the append-only Audit ledger (Tier B). This
      // is a pure side-channel record of the decision the spine just made — it does NOT
      // alter pdp/kas/decide/projection. The subject mirrors the mock's "<name> · <kind>";
      // the policy ref is the envelope's policy floor on GRANT, the I5 deny code on DENY.
      // A TRANSIENT failure records NOTHING: no decision was made, and a fake DENY row
      // would poison the ledger's meaning (mirrors the server's no-audit behavior).
      const subject = `${name} · ${env.kind}`;
      const policyFloor = compactPolicyRef(env.security?.policy_ref);
      if (res.granted) {
        recordAudit("GRANT", subject, policyFloor);
        pushToast({ title: "KAS · ACCESS GRANTED", body: `${name} decrypted in-place`, tone: "success" });
      } else if (transient) {
        pushToast({
          title: "KAS · NO DECISION",
          body: `${name} — ${res.reason ?? "key service unavailable"} (stays sealed; reopen Trust to retry)`,
          tone: "alert",
        });
      } else {
        recordAudit("DENY", subject, res.reason ?? "ACCESS_DENIED");
        pushToast({ title: "KAS · SEALED", body: `${name} — ${res.reason ?? "denied"}`, tone: "alert" });
      }
    });
  };
  const onSelect = (env: CanonicalEnvelope | null): void => {
    status.selected = env;
    status.decrypt = { state: "idle" };
    emitStatus();
    recomputeBanner(); // drop any prior decrypt-elevated marking when the selection moves
    const ref = env && realKasRef(env);
    if (!env || !ref) return;
    runDecrypt(env, ref);
  };
  const backend = new DeckBackend({
    map,
    affiliationColors,
    onPick: onSelect,
    // Bundle A reads the advanced render params (fades / error-ellipse / vector scale / drop-lines)
    // from Bundle B's persisted per-persona settings store. getSettings() loads the persisted scope
    // on boot, so render prefs survive reload. Display-only — never gates visibility (the projection
    // decides WHAT renders; these only tune HOW).
    renderSettings: getSettings(),
  });
  // Re-push on any settings change so the advanced-tab sliders + fade controls drive the picture live.
  const unsubRenderSettings = getSettingsStore().subscribe(() => backend.setRenderSettings(getSettings()));
  const engine = createRenderEngine({ backend });
  engine.setView(INITIAL_VIEW);

  // Track counts via the engine's post-ingest subscription (fires OFF the loop).
  const unsubTracks = engine.subscribe({ kinds: ["track"] }, (view) => {
    status.trackCount = view.size; // O(1) — never forces the lazy entities materialization (IVM)
    emitStatus();
  });
  // --- THE OPERATOR ALERT LOG (OPERATE). Built off the alert-kind engine subscription —
  //     i.e. the cleared-VISIBLE painted set only, so a withheld alert NEVER enters it
  //     (Glomar; no hidden-alert denominator). The read/cleared ack state is operator
  //     SELF-SCOPE state (a UI acknowledgement / dismiss), NOT a security decision: clearing
  //     an alert removes it from THIS log but never from the registry/map — a re-emit re-adds
  //     it (it re-appears UNread, which is correct: a re-fired alert deserves attention).
  //     PERSISTED PER USER (host/alertAcks.ts → the I8 per-persona settings store, leaves
  //     alerts.readIds/alerts.clearedIds): App.tsx scoped the store to the logged-in persona
  //     BEFORE startCop, so construction hydrates THIS operator's acks; every mutation
  //     persists; the live-log GC prunes persisted ids too (settle-grace-guarded, so a
  //     persisted ack is never dropped before its alert has had a chance to arrive at boot).
  //     Hydrated ids for entities this subject can't see are inert display-state — never
  //     rendered (the log is built off the painted set only), pruned in time by the GC. The
  //     firstSeen map stamps a stable Zulu time per id so the row time doesn't churn each tick. ---
  const alertAcks = createAlertAcks({
    persistence: {
      load: () => {
        const s = getSettings();
        return { readIds: s.alerts.readIds, clearedIds: s.alerts.clearedIds };
      },
      save: (snap) => {
        // Identity-scoped leaves: with no active persona the store DROPS the write (a
        // device-scope write would bleed this user's acks into every user). Fail-soft —
        // ack persistence must never take the COP down.
        try {
          const store = getSettingsStore();
          store.setSetting("alerts.readIds", snap.readIds);
          store.setSetting("alerts.clearedIds", snap.clearedIds);
        } catch (e) {
          console.error("[cop] alert-ack persist failed (state stays live in memory):", e);
        }
      },
    },
    cap: 100, // == ALERT_LOG_CAP below (the log is the reachable ack surface)
  });
  const alertFirstSeenZulu = new Map<string, string>();
  let lastAlertVisible: CanonicalEnvelope[] = [];
  const ALERT_LOG_CAP = 100;
  const recomputeAlertLog = (): void => {
    const rows: AlertRow[] = [];
    for (const env of lastAlertVisible) {
      if (alertAcks.isCleared(env.id)) continue; // operator-dismissed (self-scope), not denied
      let firstSeen = alertFirstSeenZulu.get(env.id);
      if (firstSeen === undefined) {
        firstSeen = zulu(new Date());
        alertFirstSeenZulu.set(env.id, firstSeen);
      }
      rows.push({
        id: env.id,
        label: String(env.properties.name ?? env.id),
        ...(typeof env.properties.affiliation === "string" ? { affiliation: env.properties.affiliation } : {}),
        classification: String(env.security?.classification ?? "UNCLASSIFIED"),
        time: firstSeen,
        read: alertAcks.isRead(env.id),
      });
    }
    // Newest-first by first-seen ordering is implicit (visible-set order); bound the ring.
    status.alertLog = rows.slice(0, ALERT_LOG_CAP);
  };
  const unsubAlerts = engine.subscribe({ kinds: ["alert"] }, (view) => {
    status.alertCount = view.size; // O(1) count; the log content below reads the (bounded) entities
    lastAlertVisible = view.entities;
    // Prune dismiss/read/first-seen state for alerts no longer visible (cleared by the spine /
    // aged out): a future re-emit of the SAME id then re-surfaces it UNread (re-fired =
    // attention). alertAcks.gc persists the prune and grace-guards freshly-HYDRATED ids (the
    // boot feed arrives incrementally — see host/alertAcks.ts).
    const liveIds = new Set(view.entities.map((e) => e.id));
    alertAcks.gc(liveIds);
    for (const id of [...alertFirstSeenZulu.keys()]) if (!liveIds.has(id)) alertFirstSeenZulu.delete(id);
    recomputeAlertLog();
    emitStatus();
  });
  // PERSONA RE-SCOPE — persisted acks are PER USER. On a persona switch App.tsx calls
  // handle.setIdentity(...) first (re-gates the visible set) and THEN re-scopes the I8 store
  // via setPersona → this notify fires → re-hydrate the ack sets from the NEW persona's
  // persisted state and re-derive the log + verdict. Display-only: the projection/PDP already
  // decided WHAT is visible before this runs. (Writes from our own persists keep the same
  // persona and return early here.)
  let ackPersona = getSettingsStore().getActivePersona();
  const unsubAckPersona = getSettingsStore().subscribe(() => {
    const p = getSettingsStore().getActivePersona();
    if (p === ackPersona) return;
    ackPersona = p;
    alertAcks.hydrate();
    recomputeAlertLog();
    recomputeShell(); // the verdict counts only un-dismissed alerts — re-fold for the new user
    emitStatus();
  });

  // --- Plane 2: capability-host with a HostContext bound to engine + tokens ---
  const bus = new EnvelopeBus();

  // --- The LOCAL PDP (I4 visibility). Built from the LIVE policy bundle + the
  //     logged-in identity's JWT claims; tracks the whole-picture registry. Every
  //     object is filtered through it before render; the KAS (I5) gates decrypt. ---
  const pdp = createCopPdp({ bundle: identity.bundle, claims: identity.claims });

  // --- THE SINGLE VISIBLE PROJECTION (docs/AOA-VISIBILITY-PROJECTION.md, Step 3 —
  //     FLIP TO AUTHORITATIVE, Increment 2). The projection is now the SOLE HOST
  //     writer for the visible set: its re-emit-on-subject DELTA drives the engine
  //     re-gate (retiring refilterAll/refilterKinds/RENDERED_KINDS — the diff is over
  //     IDS, so there is no kind-list to forget), and recomputeBanner/recomputeShell
  //     are now pure FOLDS of projection.bannerBasis()/censusBasis(). Per-publish host
  //     cost drops to O(1): a position-only republish yields no delta ⇒ no re-gate, no
  //     recompute, no assert (the movement firehose).
  //
  //     TWO-PDP SPLIT (the migration seam — consolidates in Increment 3). The
  //     production `pdp` (above) still backs the PER-PUBLISH painter (the capabilities
  //     write the engine directly through it — closed by Move 2 / Increment 3), the
  //     PDP-interposed data seam, and policy.decide. The projection wraps a SECOND
  //     CopPdp that is the host's derived-state authority + subject-change re-gate. The
  //     two are fed byte-identical record()/setIdentity() calls, so they stay equal by
  //     construction — proven live by assertCoherence (below) + the projection-
  //     coherence-check gate. (Moves 2 staleness-fold + bypass-closure are Increments
  //     3-4; until then the capabilities remain a second writer for per-publish paint.)
  const projection: VisibleProjection = createVisibleProjection(
    createCopPdp({ bundle: identity.bundle, claims: identity.claims }),
  );
  // The DUAL-AUTHORITY coherence tripwire (the AOA's runtime invariant, live). The
  // per-publish painter rides the production `pdp`; the host's derived state + re-gate
  // ride the projection. They MUST agree on the visible id-set; on a dev mismatch we
  // console.error LOUDLY with the diverging ids. It NEVER throws and NEVER mutates
  // state — a regression must be loud but must not itself fail open. Called only on
  // VISIBILITY-CHANGE points (delta consumer + subject switches), never per-dot — so
  // it does not reintroduce the O(visible)-per-publish cost the flip removes.
  const idSet = (envs: CanonicalEnvelope[]): Set<string> => new Set(envs.map((e) => e.id));
  const diffIds = (a: Set<string>, b: Set<string>): string[] => {
    const out: string[] = [];
    for (const id of a) if (!b.has(id)) out.push(`+${id}`);
    for (const id of b) if (!a.has(id)) out.push(`-${id}`);
    return out;
  };
  // ── THE INCREMENTAL CENSUS/BANNER HISTOGRAM (IVM Increment 2, docs/IVM-EXECUTION-PLAN.md
  //    §Increment 2). recomputeBanner/recomputeShell were O(N) FOLDS of the whole visible set
  //    on EVERY coalesced recompute; a frame that changes K of N visible rows still re-derived
  //    all N. These running aggregates are maintained INCREMENTALLY in the projection delta
  //    consumer (SYNCHRONOUSLY, per add/remove/replace) so banner/shell become O(K) reads —
  //    BYTE-IDENTICAL to the fold by construction. `histRows` mirrors the exact annotated env
  //    per visible id so a REPLACE/REMOVE can decrement the OLD env's tokens (read from the
  //    mirror BEFORE it is overwritten — the stale-key trap that would otherwise drift the
  //    banner high-water: a marking/downgrade-leak class bug). Every visible-set change flows
  //    through the projection delta (the single membership authority); the ONE non-delta wipe
  //    (clearPicture → projection.clear()) resets the histogram in lockstep (resetHistogram).
  interface SpineHist { count: number; label: string; staleCounts: Map<number, number>; staleMin: number | undefined; }
  const histRows = new Map<string, CanonicalEnvelope>(); // id → annotated visible env (the decrement pre-image)
  const clsCounts = new Map<string, number>(); // classification → count (banner max CLASS_ORDER)
  const relCounts = new Map<string, number>(); // releasability token → count (banner union: present ⇔ count>0)
  const cavCounts = new Map<string, number>(); // caveat token → count (banner union)
  const misCounts = new Map<string, number>(); // mission token → count — NON-chat visible only (isChat excluded)
  let reducedCount = 0; // visible with a coarsening leakage_profile (approximate|degraded)
  let hostileCount = 0; // visible with affiliation === "hostile"
  let trackCount_visible = 0; // visible with kind === "track"
  let histTotal = 0; // visible count (== projection rows.size)
  const alertRows = new Map<string, CanonicalEnvelope>(); // visible alerts, in projection-rows order → verdict reasons + count
  const spineHist = new Map<string, SpineHist>(); // spineKeyFor → { count, label, min finite stale ts }
  const catalogHist = new Map<string, Map<string, { label: string; count: number }>>(); // roleGroupFor → (leafId → {label,count})
  let hiddenByFilter = 0; // visible faded by the ACTIVE filter (O(1)/delta; O(N) rescan on filter/layer change)

  /** Multiset counter: +by / -by with delete-at-zero so `.keys()` is exactly the present tokens. */
  const bumpMap = (m: Map<string, number>, k: string, by: number): void => {
    const n = (m.get(k) ?? 0) + by;
    if (n <= 0) m.delete(k);
    else m.set(k, n);
  };
  /** The finite parsed stale timestamp of an env, or undefined — mirrors isStale()'s parse EXACTLY
   *  (a group is stale iff its MIN finite stale ts < Date.now(), computed at snapshot). */
  const finiteStaleTsOf = (env: CanonicalEnvelope): number | undefined => {
    const stale = env.valid_time?.stale;
    if (typeof stale !== "string") return undefined;
    const t = Date.parse(stale);
    return Number.isFinite(t) ? t : undefined;
  };
  /** A visible env's contribution to census.hiddenByFilter under the CURRENT filter+layer state
   *  (mirrors recomputeShell's reduce EXACTLY): 1 iff a filter is active, the env is not self-scoped
   *  out (layer removed/hidden), and it FAILS the filter (faded); else 0. */
  const filterContribution = (env: CanonicalEnvelope): number => {
    if (!filterPredicate) return 0;
    const key = spineKeyFor(env);
    if (layerRemoved.has(key) || layerHidden.has(key)) return 0;
    return filterPredicate(env) ? 0 : 1;
  };
  /** The banner high-water class = max CLASS_ORDER over the present classifications (default
   *  UNCLASSIFIED; CLASS_ORDER is injective so iteration order is irrelevant — byte-identical to
   *  the fold's "start UNCLASSIFIED, raise on strictly-greater"). */
  const histMaxCls = (): string => {
    let maxCls = "UNCLASSIFIED";
    for (const cls of clsCounts.keys()) {
      if ((CLASS_ORDER[cls] ?? 0) > (CLASS_ORDER[maxCls] ?? 0)) maxCls = cls;
    }
    return maxCls;
  };
  /** Fold ONE visible env INTO the aggregates (counts only — the ordered mirrors histRows/alertRows
   *  are maintained by consumeVisibleDelta so a REPLACE preserves Map insertion order like rows.set). */
  const addContrib = (env: CanonicalEnvelope): void => {
    const s = env.security;
    histTotal++;
    bumpMap(clsCounts, s.classification, 1);
    for (const r of s.releasability ?? []) bumpMap(relCounts, r, 1);
    for (const c of s.caveats ?? []) bumpMap(cavCounts, c, 1);
    if (!isChatEnvelope(env)) for (const x of s.mission ?? []) bumpMap(misCounts, x, 1);
    const lp = s?.leakage_profile;
    if (lp === "approximate" || lp === "degraded") reducedCount++;
    if (env.properties.affiliation === "hostile") hostileCount++;
    if (env.kind === "track") trackCount_visible++;
    const key = spineKeyFor(env);
    let sh = spineHist.get(key);
    if (!sh) { sh = { count: 0, label: spineLabelFor(env), staleCounts: new Map(), staleMin: undefined }; spineHist.set(key, sh); }
    sh.count++;
    const ts = finiteStaleTsOf(env);
    if (ts !== undefined) {
      sh.staleCounts.set(ts, (sh.staleCounts.get(ts) ?? 0) + 1);
      if (sh.staleMin === undefined || ts < sh.staleMin) sh.staleMin = ts;
    }
    const role = roleGroupFor(env);
    let leaves = catalogHist.get(role);
    if (!leaves) { leaves = new Map(); catalogHist.set(role, leaves); }
    const leaf = leaves.get(key);
    if (leaf) leaf.count++;
    else leaves.set(key, { label: spineLabelFor(env), count: 1 });
    hiddenByFilter += filterContribution(env);
  };
  /** Reverse addContrib for ONE env (the OLD env on a remove/replace — its exact tokens). */
  const removeContrib = (env: CanonicalEnvelope): void => {
    const s = env.security;
    histTotal--;
    bumpMap(clsCounts, s.classification, -1);
    for (const r of s.releasability ?? []) bumpMap(relCounts, r, -1);
    for (const c of s.caveats ?? []) bumpMap(cavCounts, c, -1);
    if (!isChatEnvelope(env)) for (const x of s.mission ?? []) bumpMap(misCounts, x, -1);
    const lp = s?.leakage_profile;
    if (lp === "approximate" || lp === "degraded") reducedCount--;
    if (env.properties.affiliation === "hostile") hostileCount--;
    if (env.kind === "track") trackCount_visible--;
    const key = spineKeyFor(env);
    const sh = spineHist.get(key);
    if (sh) {
      sh.count--;
      const ts = finiteStaleTsOf(env);
      if (ts !== undefined) {
        const c = (sh.staleCounts.get(ts) ?? 0) - 1;
        if (c <= 0) {
          sh.staleCounts.delete(ts);
          // The cached min stays exact: recompute from the remaining (small) distinct ts set only
          // when the min element itself was fully removed.
          if (ts === sh.staleMin) sh.staleMin = sh.staleCounts.size > 0 ? Math.min(...sh.staleCounts.keys()) : undefined;
        } else {
          sh.staleCounts.set(ts, c);
        }
      }
      if (sh.count <= 0) spineHist.delete(key);
    }
    const role = roleGroupFor(env);
    const leaves = catalogHist.get(role);
    if (leaves) {
      const leaf = leaves.get(key);
      if (leaf) { leaf.count--; if (leaf.count <= 0) leaves.delete(key); }
      if (leaves.size === 0) catalogHist.delete(role);
    }
    hiddenByFilter -= filterContribution(env);
  };
  /** Fold a projection delta INTO the histogram — the SYNCHRONOUS hook the delta consumer calls
   *  before scheduleRecompute. REMOVE/REPLACE read the OLD env from histRows BEFORE overwriting
   *  (the stale-key trap). histRows/alertRows use in-place set on REPLACE so their Map insertion
   *  order tracks the projection's rows Map (rows.set on an existing key preserves position) —
   *  keeping verdict reasons byte-identical to the fold's alerts.slice(0, 8). */
  const consumeVisibleDelta = (delta: VisibleDelta): void => {
    for (const id of delta.removes) {
      const old = histRows.get(id);
      if (!old) continue;
      removeContrib(old);
      histRows.delete(id);
      alertRows.delete(id);
    }
    for (const row of delta.replaces) {
      const old = histRows.get(row.id); // READ before overwrite
      if (old) removeContrib(old);
      addContrib(row.env);
      histRows.set(row.id, row.env);
      if (row.env.kind === "alert") alertRows.set(row.id, row.env);
      else alertRows.delete(row.id);
    }
    for (const row of delta.adds) {
      addContrib(row.env);
      histRows.set(row.id, row.env);
      if (row.env.kind === "alert") alertRows.set(row.id, row.env);
    }
  };
  /** Wipe the histogram in lockstep with a non-delta projection wipe (clearPicture). */
  const resetHistogram = (): void => {
    histRows.clear();
    clsCounts.clear();
    relCounts.clear();
    cavCounts.clear();
    misCounts.clear();
    reducedCount = 0;
    hostileCount = 0;
    trackCount_visible = 0;
    histTotal = 0;
    alertRows.clear();
    spineHist.clear();
    catalogHist.clear();
    hiddenByFilter = 0;
  };
  /** O(N) rescan of the fade counter — run ONLY when the filter/layer state changes (a mutation
   *  that flips per-row contributions with NO projection delta): applyFilter/setLayerVisible/
   *  removeLayer/addLayer. Between those, hiddenByFilter is maintained O(1)/delta. */
  const rescanHiddenByFilter = (): void => {
    let n = 0;
    if (filterPredicate) for (const env of histRows.values()) n += filterContribution(env);
    hiddenByFilter = n;
  };
  // Dev-gated, sampled (1/32) histogram tripwire. import.meta.env is undefined under headless tsx
  // (the check silently no-ops there) and DEV is false in a prod build — so this is browser-dev only.
  const HIST_CHECK: boolean = ((): boolean => { try { return import.meta.env?.DEV === true; } catch { return false; } })();
  let histCheckTick = 0;
  /** Re-fold the SAME basis O(N) and compare to the incremental aggregates — a drift (esp. the
   *  REPLACE stale-key trap) gives a wrong banner high-water the id-set check cannot see. */
  const assertHistogramMatchesFold = (where: string): void => {
    const basis = projection.censusBasis();
    const drift: string[] = [];
    let fMax = "UNCLASSIFIED";
    const fRel = new Set<string>(), fCav = new Set<string>(), fMis = new Set<string>();
    let fReduced = 0, fHostile = 0, fTrack = 0, fAlert = 0;
    const fSpine = new Map<string, number>(), fLeaf = new Set<string>();
    for (const e of basis) {
      const s = e.security;
      if ((CLASS_ORDER[s.classification] ?? 0) > (CLASS_ORDER[fMax] ?? 0)) fMax = s.classification;
      for (const r of s.releasability ?? []) fRel.add(r);
      for (const c of s.caveats ?? []) fCav.add(c);
      if (!isChatEnvelope(e)) for (const x of s.mission ?? []) fMis.add(x);
      const lp = s?.leakage_profile;
      if (lp === "approximate" || lp === "degraded") fReduced++;
      if (e.properties.affiliation === "hostile") fHostile++;
      if (e.kind === "track") fTrack++;
      if (e.kind === "alert") fAlert++;
      fSpine.set(spineKeyFor(e), (fSpine.get(spineKeyFor(e)) ?? 0) + 1);
      fLeaf.add(`${roleGroupFor(e)} ${spineKeyFor(e)}`);
    }
    const setEq = (a: Set<string>, b: Set<string>): boolean => a.size === b.size && [...a].every((x) => b.has(x));
    if (basis.length !== histTotal) drift.push(`total ${histTotal}!=${basis.length}`);
    if (fMax !== histMaxCls()) drift.push(`maxCls ${histMaxCls()}!=${fMax}`);
    if (!setEq(fRel, new Set(relCounts.keys()))) drift.push("rel-union");
    if (!setEq(fCav, new Set(cavCounts.keys()))) drift.push("cav-union");
    if (!setEq(fMis, new Set(misCounts.keys()))) drift.push("mis-union");
    if (fReduced !== reducedCount) drift.push(`reduced ${reducedCount}!=${fReduced}`);
    if (fHostile !== hostileCount) drift.push(`hostile ${hostileCount}!=${fHostile}`);
    if (fTrack !== trackCount_visible) drift.push(`track ${trackCount_visible}!=${fTrack}`);
    if (fAlert !== alertRows.size) drift.push(`alert ${alertRows.size}!=${fAlert}`);
    if (fSpine.size !== spineHist.size) drift.push(`layers ${spineHist.size}!=${fSpine.size}`);
    for (const [k, c] of fSpine) if ((spineHist.get(k)?.count ?? 0) !== c) drift.push(`spine[${k}] ${spineHist.get(k)?.count ?? 0}!=${c}`);
    let leafN = 0;
    for (const leaves of catalogHist.values()) leafN += leaves.size;
    if (fLeaf.size !== leafN) drift.push(`catalog-leaves ${leafN}!=${fLeaf.size}`);
    if (drift.length > 0) {
      console.error(`[CensusHistogram] incremental histogram diverged from a fresh fold at ${where}: ${drift.slice(0, 12).join("; ")}`);
    }
  };

  const assertCoherence = (where: string): void => {
    // The production truth is pdp.visibleEnvelopes() (the per-publish painter's basis).
    // Assert the projection's three host-facing folds (banner/rendered/census) all
    // equal it — one set in the projection by construction, the production side the
    // convention we are checking has not drifted.
    const truth = idSet(pdp.visibleEnvelopes());
    const banner = idSet(projection.bannerBasis());
    const rendered = projection.renderedIds();
    const census = idSet(projection.censusBasis());
    if (diffIds(banner, truth).length > 0) {
      console.error(
        `[VisibleProjection] bannerBasis != pdp.visibleEnvelopes() at ${where}: diverging ids ${diffIds(banner, truth).slice(0, 20).join(", ")}`,
      );
    }
    if (diffIds(rendered, truth).length > 0) {
      console.error(
        `[VisibleProjection] renderedIds != pdp.visibleEnvelopes() at ${where}: diverging ids ${diffIds(rendered, truth).slice(0, 20).join(", ")}`,
      );
    }
    if (diffIds(census, truth).length > 0) {
      console.error(
        `[VisibleProjection] censusBasis != pdp.visibleEnvelopes() at ${where}: diverging ids ${diffIds(census, truth).slice(0, 20).join(", ")}`,
      );
    }
    // HISTOGRAM-vs-FRESH-FOLD (IVM Increment 2 tripwire). The incremental aggregates MUST equal a
    // fresh O(N) fold of the SAME basis; a drift here (esp. the REPLACE stale-key trap) would give
    // a wrong banner high-water (a marking/downgrade-leak class bug) that the id-set check above
    // cannot see. DEV-ONLY + SAMPLED (the fold is O(N) — it must never run on the prod hot path).
    if (HIST_CHECK && (histCheckTick = (histCheckTick + 1) & 0x1f) === 0) assertHistogramMatchesFold(where);
  };

  // --- DOWN-FILTER (the query level). `realClaims` is the persona's actual JWT;
  //     `queryLevel` is the voluntary working level (≤ real clearance). The PDP is
  //     driven by the DOWN-CLAIMS (realClaims with classification_level lowered to
  //     the query level) — a pure subtractive view cap that can only hide MORE than
  //     the real clearance (Glomar-safe, never reveals higher content exists). ---
  let realClaims: SubjectClaims = identity.claims;
  let queryLevel: string = clearanceLevelOf(identity.claims); // default: full clearance
  // THE MULTI-DIM VIEW-AS OVERLAY (the generalization of the single-axis query level). `null` =
  // the operator is at their FULL view (no descope). A non-null spec voluntarily narrows one or
  // more axes (classification ceiling, releasability, SCI, caveat); applyViewScope folds the
  // classification level in and drives the seam. The legacy setQueryLevel writes spec.classification
  // into this same overlay, so the banner dropdown and the multi-dim panel share ONE path.
  let viewOverlay: DescopeSpec | null = null;
  // COPILOT EPHEMERALITY — the LAST effective claims applyViewScope resolved, so we can tell a
  // NARROWING (any axis lost a value, or the classification ceiling dropped) from a restore/widen
  // and purge the copilot ONLY when the view narrows (higher-scope Q&A/rows must not survive into a
  // narrower view). Seeded at full clearance (the real subject).
  let lastEffectiveClaims: SubjectClaims = realClaims;
  // --- ACTOR TERMS (ADR-0008 / F-ACTOR-1): the subtractive cap of the actor access
  //     model, threaded as a SINGLE source through recomputeQueryScope + applyViewScope.
  //     The plain HUMAN login supplies NO terms ⇒ effectiveCap === identity ⇒ ZERO
  //     behavior change (lockstep stays 40/40 by construction). This is the clear SEAM
  //     where a future C3/AI actor context (a gateway/agent/NPE path) supplies
  //     onBehalfOfOrder / npeOrder to LOWER the effective cap. The cap is purely
  //     subtractive (proven byte-identical to a genuine clamped persona by the
  //     actor-path lockstep oracle, scripts/actor-lockstep-check.ts) and never touches
  //     the JWT / KAS / rewrap. ---
  let actorTerms: ActorCapTerms = {}; // human / no-terms default
  // The actor's effective class-order cap = min(real clearance, every present actor
  // term). Identity for the human path; DENY_ORDER (fail-closed) on an unknown actor
  // class or a non-finite delegation/NPE term ⇒ empty available set + nothing visible.
  const actorCapOrder = (): number =>
    effectiveCap(CLASS_ORDER[clearanceLevelOf(realClaims)] ?? 0, actorTerms);
  const recomputeQueryScope = (): void => {
    const clearance = clearanceLevelOf(realClaims);
    // The level the actor OPERATES AT = effectiveCap(clearance, actor terms). For a
    // plain human login (no terms) this is identity ⇒ cap === clearance order. A real
    // gateway/agent/NPE path folds its reduction in via actorTerms. FAIL-CLOSED:
    // DENY_ORDER ⇒ the available set is empty (nothing operable).
    const cap = actorCapOrder();
    const npe = cap === DENY_ORDER ? "DENIED" : (LEVELS[cap] ?? clearance);
    // The SUBJECT's DESCOPED clearance marking at the current view — level + SCI compartments +
    // caveats, shaped as a HighWater so ROW 1 renders via bannerLine()/bannerTier(). Derived from
    // the NARROWED effective claims (lastEffectiveClaims): when a view-as overlay narrows an axis,
    // ROW 1 must state the DESCOPED subject ceiling (e.g. drop SCI/REL the operator stepped out of),
    // never the real, wider clearance. Casing is preserved by keeping only realClaims' proper-cased
    // values that SURVIVED the narrowing (the effective claims are lc-projected by the seam).
    // Glomar-safe: it states the subject's OWN descoped ceiling, never a data ceiling.
    const realRec = realClaims as Record<string, string | string[] | undefined>;
    const asArray = (v: string | string[] | undefined): string[] =>
      v == null ? [] : Array.isArray(v) ? v : [v];
    // The values that survived the descope on `axis`, in realClaims' original casing: intersect
    // the real (proper-cased) values with the effective (lc-projected) survivor set.
    const survived = (axis: string): string[] => {
      const kept = new Set(lcClaimValues((lastEffectiveClaims as Record<string, string | string[] | undefined>)[axis]));
      return asArray(realRec[axis]).filter((v) => kept.has(String(v).toLowerCase()));
    };
    // SCI cannot exist below TOP SECRET — only fold SCI tokens in at the TS query level.
    const sciTokens =
      queryLevel === "TOP_SECRET"
        ? [...survived("sci_control_system"), ...survived("sci_compartment")]
        : [];
    const handlingCaveats = survived("handling_caveat");
    const releasabilities = survived("releasability");
    const queryHighWater: HighWater = {
      classification: queryLevel,
      missions: sciTokens,
      caveats: handlingCaveats,
      releasability: releasabilities,
    };
    // The view-as chip / EXIT affordance the UI reads. null ⇒ FULL view (no descope). `exact`
    // (specDominatedByHeld) is true iff held ⊇ every requested value so the preview reproduces the
    // named target, false iff the descope is STRICTER than named (an unheld value / ceiling clamp).
    const viewAs =
      viewOverlay == null
        ? null
        : {
            spec: viewOverlay,
            label: viewOverlayLabel(viewOverlay),
            exact: specDominatedByHeld(realClaims, viewOverlay, pdp.bundle()),
          };
    // The operator's OWN held values per axis (proper-cased, un-narrowed) — the palette of what a
    // descope may keep. Straight from realClaims so the panel's SCI/REL/caveat controls are complete
    // at every level (queryHighWater is level-gated + merges SCI, so it can't drive them below TS).
    const heldValues: Record<string, string[]> = {
      releasability: asArray(realRec["releasability"]),
      sci_control_system: asArray(realRec["sci_control_system"]),
      sci_compartment: asArray(realRec["sci_compartment"]),
      handling_caveat: asArray(realRec["handling_caveat"]),
    };
    status.queryScope = {
      queryLevel,
      clearanceLevel: clearance,
      npeLevel: npe,
      available: cap === DENY_ORDER ? [] : LEVELS.filter((l) => (CLASS_ORDER[l] ?? 0) <= cap),
      queryHighWater,
      viewAs,
      heldValues,
    };
  };

  // --- The client-side KAS (I5): the browser rewraps AS the logged-in persona's JWT.
  //     ONE identity drives both the I4 filter (pdp, above) and the I5 release (here). ---
  initKas(identity.token);
  // KAS REACHABILITY HEARTBEAT (Phase 2 Step E): probe the live KAS on an interval and flip
  // status.kasOffline (thresholded — see kas.ts) so protected content fails closed to its OUTER
  // marking + a "KAS OFFLINE — decrypt unavailable" banner appears when the KAS is unreachable,
  // and clears when it recovers. Edge-triggered → emit only on a real transition. Torn down in
  // dispose(). Independent of the FEED link tier (feed live but KAS down, or vice-versa).
  const stopKasHeartbeat = startKasHeartbeat((offline) => {
    status.kasOffline = offline;
    emitStatus();
  });

  const host = new CapabilityHost({
    engine,
    tokens,
    // REAL capability-seam policy (ADR-0008 C1): gate every declared permission against
    // the live entitlement PDP — same bundle + current down-claims as the render filter.
    // Legacy-permissive on label-less resources. TDFLite-safe: never touches the
    // JWT/KAS/rewrap; strictly subtractive.
    policy: {
      decide: (req: PolicyRequest): Promise<PolicyDecision> =>
        Promise.resolve(decideForResource(req.resource, pdp.bundle(), pdp.currentClaims())),
    },
    data: {
      // PDP-INTERPOSED data seam (ADR-0008 C1, gap #1): record the WHOLE picture, but
      // DELIVER to a capability/AI ONLY the cleared-visible envelopes. A denied envelope
      // is never delivered — no marker, no error, no count (Glomar). pdp.evaluate reads
      // the PDP's current claims (the single source the render path uses). publish is
      // unchanged — gating is subtractive on the receive side only.
      subscribe: (filter, cb) =>
        bus.subscribe(filter, (env) => {
          pdp.record(env);
          // isVisibleCached decides each envelope ONCE per claims-epoch and shares the bit across
          // ALL subscribers — O(S×E) → O(E) per tick (the measured S=50 cliff was a 7.3s frame
          // overrun; this makes it flat). Byte-identical to evaluate().presentation.visible.
          if (pdp.isVisibleCached(env)) cb(env);
        }),
      publish: (env) => bus.publish(env),
    },
  });

  // --- High-water marking: derive the dual classification banner from the
  //     VISIBLE entities only (an object the operator can't see must NOT leak its
  //     classification through the banner). Sourced from the PDP's visible set, so
  //     it re-derives when clearance changes.
  let lastBannerKey = "";
  const recomputeBanner = (): void => {
    // O(K) READ of the incremental census/banner histogram (maintained SYNCHRONOUSLY in the
    // projection delta consumer, above) instead of an O(N) fold of projection.bannerBasis().
    // BYTE-IDENTICAL: max CLASS_ORDER over the present classifications; releasability/caveats
    // unions over ALL visible (a token is present iff its count>0); mission union over NON-chat
    // visible only — misCounts is fed non-chat-only in addContrib, mirroring the isChatEnvelope
    // exclusion the fold applied (chat "mission" tokens are room NAMES, not markings, and must
    // never leak into the banner's mission segment, while a chat's classification/releasability/
    // caveats still raise the high-water — never under-mark / downgrade-leak).
    let maxCls = histMaxCls();
    const rel = new Set<string>(relCounts.keys());
    const cav = new Set<string>(cavCounts.keys());
    const mis = new Set<string>(misCounts.keys());
    // Highest-classification-on-screen is DYNAMIC: a DECRYPTED inner payload (P4) is now
    // rendered in the inspector and can exceed every visible outer floor — fold it in so
    // the banner marks the screen correctly (and drops back on deselect / selection move).
    if (status.decrypt.state === "granted" && status.decrypt.marking) {
      const dm = status.decrypt.marking;
      if ((CLASS_ORDER[dm.classification] ?? 0) > (CLASS_ORDER[maxCls] ?? 0)) maxCls = dm.classification;
      for (const r of dm.releasability ?? []) rel.add(r);
      for (const c of dm.caveats ?? []) cav.add(c);
      for (const x of dm.mission ?? []) mis.add(x);
    }
    const key = `${maxCls}|${[...mis].sort().join(",")}|${[...cav].sort().join(",")}|${[...rel].sort().join(",")}`;
    if (key === lastBannerKey) return;
    lastBannerKey = key;
    status.highWater = {
      classification: maxCls,
      missions: [...mis].sort(),
      caveats: [...cav].sort(),
      releasability: [...rel].sort(),
    };
    emitStatus();
  };
  // --- Shell-contract derivations: recompute the visible-only aggregates the
  //     named-IA shell reads (verdict / census / spine / pictureSummary). CRITICAL
  //     GLOMAR INVARIANT: computed over pdp.visibleEnvelopes() ONLY — the same
  //     already-cleared source the banner uses (cop.ts recomputeBanner). A withheld
  //     object contributes NOTHING (no count, no row, no reason). Mirrors the banner:
  //     no early-out key here — these objects are cheap to rebuild and always fresh.
  //     Spec: docs/ux/SHELL-COMPOSITION.md §3 (population notes), §7 (denial receipt).
  const recomputeShell = (): void => {
    // O(K) READS of the incremental census/banner histogram (maintained SYNCHRONOUSLY in the
    // projection delta consumer, above) instead of an O(N) fold of projection.censusBasis().
    // Each aggregate is BYTE-IDENTICAL to the prior fold by construction; the assertCoherence
    // histogram-vs-fresh-fold extension proves it live (dev/sampled).

    // The cleared-visible denominator (M) is the WHOLE visible set (histTotal == rows.size).
    // `shown` (N) subtracts only what an active in-place FILTER fades — never a PDP-hidden
    // object (that would leak existence). A layer hidden/removed by the operator is a deliberate
    // self-scope, counted as shown-but-off (it is not a denial).
    const total = histTotal;
    const layers = spineHist.size; // distinct spine keys over ALL visible (== the fold's layerKeys.size)

    // CENSUS — hiddenByFilter counts visible objects faded by the ACTIVE FILTER ONLY (not layer
    // self-scoped members). It is maintained O(1)/delta while the filter+layer state is stable and
    // rescanned O(N) ONLY on applyFilter/setLayerVisible/removeLayer/addLayer (a state change that
    // flips per-row fade contributions with NO projection delta); 0 when no filter is active.
    const reduced = reducedCount;
    status.census = {
      layers,
      shown: total - hiddenByFilter,
      total,
      hiddenByFilter,
      released: total - reduced,
      reduced,
      requestable: 0,
    };

    // VERDICT — shape-primary face from the visible alert/health picture, EXCLUDING operator-
    // DISMISSED alerts (self-scope: dismissing quiets the glyph/flash for THIS operator only;
    // the objects stay on the map/registry and a re-emit re-raises the verdict — a display
    // acknowledgement, never a security decision). The reasons[] are the first 8 un-dismissed
    // visible alerts, in the projection's rows insertion order (add/replace preserve position).
    let alertN = 0;
    const reasons: VerdictState["reasons"] = [];
    for (const env of alertRows.values()) {
      if (alertAcks.isCleared(env.id)) continue;
      alertN++;
      if (reasons.length < 8) {
        reasons.push({ id: env.id, label: String(env.properties.name ?? env.id) });
      }
    }
    const face: VerdictState["face"] =
      alertN > 0 ? "alert" : hostileCount > 0 ? "watch" : "quiet";
    const urgency: VerdictState["urgency"] =
      alertN >= 3 ? "flash" : alertN === 2 ? "immediate" : alertN === 1 ? "priority" : "routine";
    status.verdict = { face, urgency, reasons };

    // SPINE — host-generated active-feed rows, one per visible source/kind group; a REMOVED group
    // is dropped entirely. count + label are maintained per key (spineHist). `stale` is TIME-
    // dependent, so it is computed at THIS snapshot from the group's MIN finite stale timestamp vs
    // Date.now() — identical to the fold's "any member isStale()" at recompute time (NOT delta-driven).
    const now = Date.now();
    status.spine = [...spineHist.entries()]
      .filter(([id]) => !layerRemoved.has(id))
      .map(([id, sh]) => ({
        id,
        label: sh.label,
        visible: !layerHidden.has(id),
        count: sh.count,
        health: (sh.staleMin !== undefined && sh.staleMin < now ? "stale" : "ok") as LayerRow["health"],
        opacity: layerOpacity.get(id) ?? 1,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    // CATALOG — the +Add universe: role-grouped over the CLEARED-VISIBLE set only (a withheld leaf
    // is simply ABSENT — Glomar). Maintained per role→leaf (catalogHist); a leaf can appear under
    // more than one role, so it is NOT derivable from spineHist. A removed group still reappears
    // here as an addable leaf (re-add is idempotent).
    status.catalog = [...catalogHist.entries()]
      .map(([group, leaves]) => ({
        group,
        leaves: [...leaves.entries()]
          .map(([id, leaf]) => ({ id, label: leaf.label }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      }))
      .sort((a, b) => a.group.localeCompare(b.group));

    // EDITION — the named edition of reality + fidelity + link tier (Cartouche). A
    // mock feed is a NAMED degraded edition (operators trust a named lost state); a
    // live feed is the full connected edition. Honest, never a gap.
    status.edition =
      status.feedMode === "live"
        ? { name: "LIVE", fidelity: "full", linkTier: "connected" }
        : status.feedMode === "mock"
          ? { name: "EXERCISE", fidelity: "synthetic", linkTier: "local" }
          : status.feedMode === "offline"
            ? // NO synthetic fallback: an unreachable live feed is an HONEST dark
              // state (zero dots), named so the operator trusts the lost link.
              { name: "NO FEED", fidelity: "no data", linkTier: "offline" }
            : { name: "LINKING", fidelity: "partial", linkTier: "acquiring" };

    // PICTURE-SUMMARY — the right-gutter Ledger rest state. Visible-only headline +
    // a few honest rollup rows (never the hidden universe).
    //
    // STAT CARDS (no-selection PICTURE SUMMARY): RELEASED / REDUCED / REQUESTABLE /
    // HOSTILE·KNOWN — all over the CLEARED-VISIBLE set only (Glomar-safe). RELEASED
    // is the whole visible count; REDUCED is the subset released at lower fidelity
    // (a leakage_profile that coarsens/approximates/degrades the payload — NOT a
    // denial: a reduced object is still visible). HOSTILE·KNOWN is the visible
    // hostile count. REQUESTABLE is a FORWARD-LOOKING placeholder (0): a future
    // KAS-version PDP path will populate it; today there is no real deny/request
    // path so the slot renders empty rather than fabricating a hidden denominator.
    // REDUCED / REQUESTABLE reuse the SAME folds the census triad computed above (one
    // source of truth — released/reduced/requestable are identical across the pill + the
    // picture-summary stat cards). REQUESTABLE stays the forward-looking 0 placeholder.
    // NOTE the RELEASED stat card value is `total` (the whole visible count), NOT total-reduced —
    // deliberately distinct from census.released (= total - reduced). All O(1)/O(K) histogram reads.
    status.pictureSummary = {
      headline: total === 0 ? "No contacts" : `${total} contact${total === 1 ? "" : "s"} on the map`,
      rows: [
        { k: "Tracks", v: String(trackCount_visible) },
        { k: "Alerts", v: String(alertN) },
        { k: "Layers", v: String(layers) },
        { k: "Hostile", v: String(hostileCount) },
      ],
      stats: [
        { tone: "released", label: "RELEASED TO YOU", value: total },
        { tone: "reduced", label: "REDUCED FIDELITY", value: reduced },
        { tone: "requestable", label: "REQUESTABLE", value: 0 },
        { tone: "hostile", label: "HOSTILE · KNOWN", value: hostileCount },
      ],
      requestable: 0,
    };

    emitStatus();
  };

  // --- THE SOLE-HOST-WRITER seam: paint one ALREADY-ANNOTATED projection row to the
  //     engine, layering the operator's OWN self-scope (layer hide/remove + FILTER
  //     fade) on top of the projection's clearance decision. A hidden/removed/faded
  //     object is removed from the engine but STAYS in the projection (reversible, and
  //     never a denial — the cleared-visible denominator is unchanged). The row's env
  //     is annotated by the projection (annotateForRender), so paint is uniform: every
  //     host write goes through here, never a raw or re-derived envelope. ---
  const paintRow = (env: CanonicalEnvelope): void => {
    const key = spineKeyFor(env);
    const off = layerRemoved.has(key) || layerHidden.has(key) || (filterPredicate ? !filterPredicate(env) : false);
    if (off) engine.removeEntities([env.id]);
    else engine.upsertEntities([env]);
  };

  // Re-assert the operator's self-scope across the WHOLE visible picture (all kinds — no
  // kind-list to forget). Called when a self-scope override changes (hide/show/remove/
  // filter) or after a subject switch — NOT per-publish. GEOMETRY/payload is read LIVE
  // from the production pdp's visible set (its membership is projection-coherent — proven
  // by assertCoherence), NOT the projection's rows: a projection row's env geometry is
  // intentionally FROZEN on the movement firehose (a position-only republish emits no
  // delta, so the row is never rebuilt), so re-painting from a frozen row would snap a
  // moving track back to its ORIGIN. The live registry is refreshed on every publish, and
  // the host's per-publish painter + the delta consumer already paint live geometry — this
  // keeps the self-scope re-paint live too. (When the projection owns geometry/staleness —
  // Increment 4 — this can fold back onto renderedRows.)
  const applyLayerGate = (): void => {
    for (const env of pdp.visibleEnvelopes()) paintRow(annotateForRender(env, pdp.evaluate(env)));
  };

  // Drive the engine from a projection DELTA — the AOA's "the host's projection is the
  // only thing that calls engine.upsert/remove" (sole host writer). Removes vanished
  // ids; (re)paints added/replaced rows through the self-scope-aware paintRow. This is
  // what retires refilterAll: on a subject switch the projection emits the add/remove
  // batch and the engine re-gates by ID, so a capability's non-track kind can never be
  // forgotten by a stale RENDERED_KINDS ∪ list (the Phase-4 leak, now impossible).
  const applyDeltaToEngine = (delta: VisibleDelta): void => {
    // RE-GATE SNAP (the down-filter "fly-in" fix): removals SHIFT the survivors' indices in the
    // engine's snapshot arrays, and deck.gl position transitions are INDEX-aligned — a survivor
    // at a shifted index would GLIDE from the removed entity's old GPU position ("icons fly in
    // from all directions" on a clearance step-down). Tell the backend to SNAP the next data
    // frame; the motion glide resumes on the following frame. Position-only motion emits no
    // delta, so the firehose never lands here.
    if (delta.removes.length > 0) backend.notifyMembershipChange();
    for (const id of delta.removes) engine.removeEntities([id]);
    for (const row of delta.adds) paintRow(row.env);
    for (const row of delta.replaces) paintRow(row.env);
  };

  // A transient flag set by the delta consumer (below) and read by the per-publish painter,
  // so the two NEVER double-paint the same publish: on a visibility-CHANGING publish the
  // consumer paints (adds/replaces) and the per-publish painter skips; on a position-only
  // move (no delta) the per-publish painter is the one that repaints the new geometry.
  let deltaFiredThisPublish = false;

  // THE PROJECTION DELTA CONSUMER — the single place the host reacts to a VISIBILITY change
  // (a publish that adds/removes/re-marks a visible row, OR a subject switch via onSubject).
  // It drives the engine re-gate (applyDeltaToEngine) + the banner/shell folds. A position-
  // only republish emits NO delta ⇒ this never fires ⇒ O(1) movement firehose. assertCoherence
  // runs here, where a real visibility change just happened (never per-dot).
  // COALESCED RECOMPUTE — banner/census/coherence are O(visible-N) FOLDS; running them PER delta
  // makes a BURST of visibility changes O(N²) (50k new entities = 50k deltas × O(N) — the bulk-
  // ingest cliff). The per-delta engine PAINT (applyDeltaToEngine, O(1)) stays immediate so each
  // new/removed row renders, but the folds are debounced to ONE recompute per animation frame: a
  // burst of N deltas collapses to a single O(N) recompute. Eventually-consistent (the banner/
  // census reflect the post-burst resting state, one frame late — imperceptible) and benefits the
  // real feed too (a batch of new tracks → one recompute). The position-only firehose still emits
  // NO delta, so this consumer never fires on movement. assertCoherence still runs (once per frame,
  // on the resting state) — the tripwire that proves the projection's visible set == pdp truth.
  let recomputeScheduled = false;
  const flushRecompute = (): void => {
    recomputeScheduled = false;
    recomputeBanner();
    recomputeShell();
    assertCoherence("delta");
  };
  const scheduleRecompute = (): void => {
    if (recomputeScheduled) return;
    recomputeScheduled = true;
    if (typeof requestAnimationFrame !== "undefined") requestAnimationFrame(flushRecompute);
    else queueMicrotask(flushRecompute); // headless/test fallback (no rAF)
  };
  projection.subscribe((delta: VisibleDelta) => {
    deltaFiredThisPublish = true;
    // IVM Increment 2: fold this visibility delta INTO the census/banner histogram SYNCHRONOUSLY,
    // BEFORE scheduleRecompute — so the coalesced recomputeBanner/recomputeShell (and any
    // synchronous reader, e.g. maybeAutoWiden) read an up-to-date O(K) aggregate, not an O(N) fold.
    consumeVisibleDelta(delta);
    applyDeltaToEngine(delta);
    scheduleRecompute();
  });

  // The SINGLE host publish-handler: record + project + PAINT. (Increment 3 — the host is now
  // the SOLE engine writer; the capabilities no longer paint.)
  //  1. pdp.record — the canonical store (full picture); FIRST so the visibility bit and the
  //     consumer's assertCoherence read the current truth.
  //  2. projection.onRecord — emits a DELTA iff the publish CHANGED a visible row (new id /
  //     re-mark / visibility flip); the consumer above then re-gates + recomputes AND sets
  //     deltaFiredThisPublish (so for a visibility-changing publish the CONSUMER is the painter).
  //  3. PER-PUBLISH PAINTER — the FIREHOSE painter. It fires ONLY when onRecord emitted NO
  //     delta (a position-only move / byte-equal republish of a visible object): there the
  //     consumer did not repaint, so this forwards the LIVE env (new geometry) to the engine.
  //     This is the work the tracks/multiInt capabilities used to do, now relocated to the
  //     sole host writer + made uniform (annotateForRender). paintRow applies the operator
  //     self-scope; removal of a now-hidden id is the consumer's remove delta (step 2). O(1).
  bus.subscribe({}, (env) => {
    deltaFiredThisPublish = false;
    pdp.record(env);
    projection.onRecord(env);
    if (!deltaFiredThisPublish && pdp.isVisibleCached(env)) {
      paintRow(annotateForRender(env, pdp.evaluate(env)));
    }
  });

  // NOTE — refilterKinds/refilterAll are GONE (AOA Step 3). The whole-picture clearance
  // re-filter is now the projection's onSubject DELTA: it diffs ALL recorded ids against
  // the new subject column and the delta consumer re-gates the engine by id (+ folds the
  // banner/census + re-asserts self-scope). There is no RENDERED_KINDS ∪ capability-kind
  // list to keep current — a new capability kind is covered by construction (the Phase-4
  // in-session down-filter leak can no longer exist). The one explicit step a subject
  // switch still owns is re-asserting the operator self-scope on top of the re-gate, via
  // reapplySelfScope() below (the delta consumer paints each row self-scope-aware, but a
  // no-delta switch — same visible set — must still settle self-scope for changed rows).
  const reapplySelfScope = (): void => {
    if (layerHidden.size > 0 || layerRemoved.size > 0 || filterPredicate) applyLayerGate();
  };

  // Re-gate the WHOLE picture for a new subject column — the ONE atomic, ORDER-CRITICAL
  // operation every subject switch (persona login + down-filter, incl. the deny path) runs.
  // The order is LOAD-BEARING and enforced HERE (not duplicated per call site, where a
  // future edit could swap it): (1) production pdp.setIdentity FIRST so the data seam,
  // policy.decide, and the caller's selection-drop check (pdp.evaluate) all read the new
  // claims; (2) projection.onSubject — its delta consumer re-gates the engine by id-diff +
  // folds the banner/census; (3) reapplySelfScope re-asserts the operator self-scope on
  // top. (assertCoherence would also scream if 1 and 2 were ever swapped — the two pdps'
  // visible sets would diverge — but enforcing the order in one place is the real guard.)
  const switchSubject = (nextClaims: SubjectClaims): void => {
    pdp.setIdentity(nextClaims);
    projection.onSubject(nextClaims);
    reapplySelfScope();
  };

  // H2 — the chat-store DOWN-FILTER rebuild hook. Declared here (before applyViewScope) but
  // ASSIGNED its real body after the chat capability + chatKeyAccess are created below. A no-op
  // until then (the down-filter is only reachable via a post-boot handle call, by which point
  // it is wired). Clears the chatKeyAccess plaintext cache + rebuilds the chat store over the
  // down-visible census, mirroring setIdentity's discipline on the SAME-persona down view.
  let rebuildChatForDownView: (claimsForView: SubjectClaims) => void = () => {};

  // Apply the current VIEW-AS OVERLAY: descope the subject over the live policy bundle (the ONE
  // seam — descopedClaimsFor, never narrowHeld directly), then re-resolve the whole picture. The
  // overlay generalizes the single-axis query level into a MULTI-DIM step-down (classification
  // ceiling + releasability + SCI + caveat). Down-only by construction: the seam only ever hides
  // MORE than the real clearance (unheld/omitted spec values contribute nothing). null/{} overlay
  // ⇒ effective === realClaims (the full-view identity path — regression-free).
  const applyViewScope = (): void => {
    // Fold the ACTOR CAP into the classification clamp: the ceiling is the MIN of the voluntary
    // requested level and the actor's effective cap (itself ≤ real clearance — so the clamp-to-
    // real-clearance guard is PRESERVED, never exceeded). Plain human login ⇒ actorCapOrder() ===
    // real-clearance order ⇒ this collapses to min(requestedLevel, realClearance).
    const actorCap = actorCapOrder();
    // FAIL-CLOSED (VERBATIM from the single-axis path): an unknown actor class / non-finite term ⇒
    // DENY_ORDER ⇒ drive the PDP with a classification level NO object can satisfy ⇒ empty available
    // + nothing visible. We never clamp to a real level on deny (that would fail OPEN).
    if (actorCap === DENY_ORDER) {
      const denyClaims: SubjectClaims = {
        ...realClaims,
        classification: "\u0000DENIED",
        classification_level: "\u0000DENIED",
      };
      // Re-gate everything to the deny claims (removes EVERYTHING under a level no object
      // can satisfy). switchSubject sets the production pdp first, so the selection-drop
      // check (pdp.evaluate) below reads the deny claims.
      switchSubject(denyClaims);
      // H2 — mirror setIdentity's chat discipline at the DOWN (here: deny) level: clear the
      // chatKeyAccess plaintext cache + rebuild the chat store over the now-empty down-visible
      // census, so a previously-decoded body cannot survive into the deny view.
      rebuildChatForDownView(denyClaims);
      // COPILOT EPHEMERALITY — deny is the hardest narrowing (nothing visible). Purge the copilot
      // thread + working set so no higher-scope Q&A turn or row survives into the deny view (mirrors
      // the chat-store rebuild above). lastEffectiveClaims becomes the deny claims so a later widen
      // (recover from deny) is correctly seen as a restore.
      copilotApi.reset();
      lastEffectiveClaims = denyClaims;
      const denied = status.selected;
      if (denied && !pdp.evaluate(denied).presentation.visible) onSelect(null);
      recomputeQueryScope();
      emitStatus();
      assertCoherence("view-overlay:deny");
      return;
    }
    // The requested classification ceiling: the overlay's classification axis (if any), else full
    // clearance (no level narrowing). Clamp to min(requested, actor cap) — always ≤ real clearance.
    const clearanceOrder = CLASS_ORDER[clearanceLevelOf(realClaims)] ?? 0;
    const overlayLevelRaw = (viewOverlay as Record<string, string | string[] | undefined> | null)?.["classification"];
    const overlayLevel =
      overlayLevelRaw == null
        ? null
        : String(Array.isArray(overlayLevelRaw) ? overlayLevelRaw[0] : overlayLevelRaw).toUpperCase();
    const requestedOrder = overlayLevel != null ? (CLASS_ORDER[overlayLevel] ?? 0) : clearanceOrder;
    const cap = Math.min(requestedOrder, actorCap);
    queryLevel = LEVELS[cap];
    // Assemble the spec handed to the seam: the overlay's non-classification axes PLUS the clamped
    // classification ceiling. Include a classification axis ONLY when the level actually narrows
    // (the overlay named a level, OR the actor cap clamps below full clearance); otherwise leave the
    // overlay untouched so a null/{} overlay flows through the seam as the IDENTITY (=== realClaims)
    // — the regression-free full-view path. The seam mirrors classification→classification_level +
    // strips the legacy key; we do NOT re-do that here.
    let spec: DescopeSpec | null = viewOverlay;
    if (overlayLevel != null || cap < clearanceOrder) {
      spec = { ...(viewOverlay ?? {}), classification: [queryLevel] };
    }
    // THE SINGLE DESCOPE STEP. effective = realClaims narrowed by the spec (lc-projected, down-only
    // by construction). A null/{} spec returns realClaims UNCHANGED (identity).
    const effective = descopedClaimsFor(realClaims, spec, pdp.bundle());
    // Re-gate everything to the descoped claims (the Phase-4 leak fix, now structural: the id-diff
    // re-gates ALL kinds incl. capability sensors). switchSubject sets the production pdp first, so
    // the selection-drop check (pdp.evaluate) reads effective; BOTH pdps get the byte-identical
    // effective object so assertCoherence stays green.
    switchSubject(effective);
    // H2 — VIEW-SCOPE CHAT DISCIPLINE (the previously-decoded-HIGH-body leak fix). Unlike
    // setIdentity, the view-scope does NOT switch the JWT (so setKasToken's cache-clear never fires)
    // — yet a SECRET/REL-scoped body decoded at full clearance still sits in the chat roomStore and
    // would be re-painted in the narrower view + leak via lastMessagePreview while the banner reads
    // descoped. Clear the chatKeyAccess plaintext cache AND rebuild the chat store over ONLY the now-
    // visible census (a room/message absent at the descoped view is ABSENT from the store). On a
    // WIDEN this runs again and the body returns iff the wider view re-permits it. Single membership
    // authority: the chat store folds off the SAME census the map/banner do.
    rebuildChatForDownView(effective);
    // COPILOT EPHEMERALITY — purge the copilot thread + working set on ANY NARROWING (the effective
    // claims lost a held value on some axis, or the classification ceiling dropped) vs the LAST
    // effective view. A widen/restore keeps the conversation (no leak: cleared for more, not less).
    // reset() is the FULL security wipe (thread + runtime driver state + working set) — the same
    // purge the level path used — so no higher-scope reasoning OR working-set row survives into the
    // narrower view. Fires on a same-level releasability/SCI/caveat narrowing too (an order-only
    // check would miss those).
    if (isViewNarrowing(lastEffectiveClaims, effective)) {
      copilotApi.reset();
      pushToast({
        title: "COPILOT · WIPED",
        body: `View narrowed to ${viewOverlay ? viewOverlayLabel(viewOverlay) || queryLevel : queryLevel} — copilot state cleared (no higher-scope reasoning retained).`,
        tone: "info",
      });
    }
    lastEffectiveClaims = effective;
    // A now-out-of-view selection must drop (existence not revealed). onSelect(null) also resets the
    // decrypt state to idle + recomputes the banner, so a previously-decrypted inner marking can no
    // longer keep ROW 2 elevated above the descoped view — matching the single-axis path exactly (a
    // still-visible selection keeps its granted decrypt state, as before).
    const sel = status.selected;
    if (sel && !pdp.evaluate(sel).presentation.visible) onSelect(null);
    recomputeQueryScope();
    emitStatus();
    assertCoherence("view-overlay");
  };

  // Set / MERGE / CLEAR the view-as overlay, then re-apply. `null` ⇒ CLEAR (EXIT view-as ⇒ full
  // view). Otherwise MERGE the partial spec into the active overlay: a named axis narrows (replaces
  // that axis), an EMPTY-ARRAY value ([]) REMOVES the axis (widens it back to full), and omitted
  // axes keep their current overlay value. An overlay that merges down to {} collapses to null (a
  // fully-widened overlay is the full view). Both handle.setQueryLevel and handle.setViewOverlay go
  // through here so the single-axis dropdown and the multi-dim panel share ONE path.
  const applyViewOverlay = (spec: DescopeSpec | null): void => {
    if (spec === null) {
      viewOverlay = null;
    } else {
      const merged: DescopeSpec = { ...(viewOverlay ?? {}) };
      for (const [k, v] of Object.entries(spec)) {
        if (Array.isArray(v) && v.length === 0) delete merged[k]; // widen this axis back to full
        else merged[k] = v;
      }
      viewOverlay = Object.keys(merged).length === 0 ? null : merged;
    }
    applyViewScope();
  };

  // --- THE TIME WINDOW (recency) — the "one global time owner" (docs/AOA-STALENESS-TIME-
  //     WINDOW.md, Increment 4). A JUST-IN-TIME, PANE-OF-GLASS recency filter: the registry
  //     keeps everything; this only decides what is PAINTED, via the projection (which now
  //     owns clearance ∩ recency, so census == paint by construction). NEVER fed to the feed/
  //     source. Log-scale width stops; default "last 30 min" with an auto-widen if the picture
  //     is too sparse, then the operator owns it. ---
  // Log-scale recency stops from MINUTES → ALL TIME (Will's spec). The default is "30 min";
  // auto-widen only ever steps UP from there. ◂ can narrow into the minutes (where a slow-
  // updating track visibly ages out); ▸ widens to all.
  const TIME_STOPS: { ms: number; label: string }[] = [
    { ms: 5 * 60_000, label: "5 min" },
    { ms: 15 * 60_000, label: "15 min" },
    { ms: 30 * 60_000, label: "30 min" },
    { ms: 2 * 3_600_000, label: "2 hr" },
    { ms: 6 * 3_600_000, label: "6 hr" },
    { ms: 86_400_000, label: "1 d" },
    { ms: 3 * 86_400_000, label: "3 d" },
    { ms: Infinity, label: "all time" },
  ];
  const DEFAULT_TIME_IDX = 2; // "30 min"
  let timeStopIdx = DEFAULT_TIME_IDX;
  let timeManual = false; // once the operator dials it, auto-widen never fires again
  let timeAnchorMs: number | null = null; // null = LIVE (anchor follows now); a number = scrubbed-to-past

  /** Push the current time owner into the projection: the live clock `now` + the recency
   *  window [lo, hi] over lastSeen. Live ⇒ hi=+Infinity, lo = now − width; scrubbed ⇒ hi =
   *  anchor, lo = anchor − width; all-time width ⇒ lo = -Infinity (no recency filtering).
   *  Drives projection.onTime → the delta consumer (re-gate + recompute + assert). */
  const applyTimeWindow = (): void => {
    const width = TIME_STOPS[timeStopIdx].ms;
    const now = Date.now();
    const anchor = timeAnchorMs ?? now;
    const lo = width === Infinity ? -Infinity : anchor - width;
    const hi = timeAnchorMs === null ? Infinity : anchor;
    projection.onTime(now, lo, hi);
  };

  /** The time-window status the slider control renders. */
  const recomputeTimeWindow = (): void => {
    status.timeWindow = {
      widthMs: TIME_STOPS[timeStopIdx].ms === Infinity ? -1 : TIME_STOPS[timeStopIdx].ms,
      widthLabel: TIME_STOPS[timeStopIdx].label,
      live: timeAnchorMs === null,
      stopIndex: timeStopIdx,
      stopCount: TIME_STOPS.length,
    };
  };

  /** AUTO-WIDEN: if the painted picture is too sparse (< 20), step the recency width UP until
   *  ≥20 painted or all-time — a one-time "show me enough context" heuristic, run at boot +
   *  on persona switch, NEVER against a width the operator set by hand. */
  const MIN_AUTO_ITEMS = 20;
  const maybeAutoWiden = (): void => {
    if (timeManual) return;
    // Count what the operator ACTUALLY sees — status.census.shown (cleared ∩ in-window minus
    // any active in-place FILTER) — NOT the raw projection set (which would over-count past a
    // filter). The delta consumer's recompute is now COALESCED to one run per animation frame
    // (scheduleRecompute), which is asynchronous + a frame too late for this SYNCHRONOUS loop —
    // so refresh census.shown DIRECTLY here. recomputeShell is O(visible), and this runs only at
    // boot / persona-switch for at most TIME_STOPS steps (~5), so the direct calls are cheap.
    recomputeShell(); // fresh baseline for the first condition check
    let widened = false;
    while (timeStopIdx < TIME_STOPS.length - 1 && status.census.shown < MIN_AUTO_ITEMS) {
      timeStopIdx++;
      applyTimeWindow();
      recomputeShell(); // SYNC refresh so the next condition sees this width's real count
      widened = true;
    }
    if (widened) {
      recomputeTimeWindow();
      emitStatus();
    }
  };

  // Seed the time owner NOW (before any publish below), so arriving objects are stamped
  // lastSeen=now and land INSIDE the default 30-min window (not filtered out as "too old").
  applyTimeWindow();
  recomputeTimeWindow();
  // The coarse AGE-OUT TICK (~1Hz — recency is minute-scale; per-frame is pointless): advances
  // the time owner's `now` so aged-out objects drop and re-published ones return. Cheap when
  // nothing crosses the window edge (reproject is O(N) memo lookups; no delta ⇒ no recompute).
  const ageOutTimer = window.setInterval(applyTimeWindow, 1_000);

  // Both capabilities are now CONTRIBUTE-ONLY (Increment 3): they declare a layer (and the
  // multi-INT one also publishes its dataset + typed commands) and NEVER write the engine.
  // The HOST paints every cleared-visible envelope (the per-publish painter above) + re-gates
  // on subject change (the projection). tracksCapability no longer imports pdp.ts or takes a
  // pdp — it is as pure as the dogfood capability now.
  host.register({ capability: createTracksCapability() });
  await host.activate("scop:cap:tracks");

  // THE DOGFOOD CAPABILITY (0.2a CORE exit gate): a real multi-INT capability built against
  // ONLY the public C3 SDK (no host internals). Registered WITH its CapabilityManifest and
  // activated on the SAME CapabilityHost. It publishes its bundled multi-INT dataset; the
  // host records it, runs it through the PDP, and paints the cleared-visible slice — so the
  // picture is clearance-filtered host-side WITHOUT the capability ever calling a PDP or the
  // engine. Additive: distinct `sensor` kind / `multi-int` layer; lockstep unchanged.
  host.register({ capability: createMultiIntCapability(), manifest: MULTI_INT_MANIFEST });
  await host.activate(MULTI_INT_CAP_ID);

  // THE DENSITY CAPABILITY (0.2b "haystack"): publishes ~153 REAL, mundane clear-shell Hormuz
  // movement tracks onto the bus so sealed/protected objects sit in a SEA OF REAL TRACKS rather
  // than an empty map. PUBLISH-ONLY like the dogfood capability (no manifest, no engine write):
  // the host records each envelope, runs it through the PDP, and paints the cleared-visible
  // slice on the existing `track` layer. Additive — no new kind/layer, lockstep unchanged.
  host.register({ capability: createDensityCapability() });
  await host.activate(DENSITY_CAP_ID);

  // DEV STRESS LOAD GENERATOR — strictly gated render/pipeline benchmark. Activates ONLY when the
  // URL carries ?stress=N AND the build allows it: the vite DEV server (import.meta.env.DEV), or an
  // explicit benchmark dist built with VITE_ALLOW_STRESS=1 (so the MINIFIED render path can be
  // measured without the dev-server penalty). A NORMAL production build sets neither flag, so it can
  // never activate. ?stress=N publishes N synthetic clear-shell tracks (?stressMove=M move each
  // tick, rest static — the realistic "render N dots" case). Spine untouched (clear-shell, no PDP/KAS).
  const stressAllowed =
    import.meta.env.DEV || import.meta.env.VITE_ALLOW_STRESS === "1";
  const stressN =
    stressAllowed && typeof window !== "undefined"
      ? Number(new URLSearchParams(window.location.search).get("stress") ?? "0")
      : 0;
  if (Number.isFinite(stressN) && stressN > 0) {
    const moveParam = Number(new URLSearchParams(window.location.search).get("stressMove") ?? "NaN");
    host.register({
      capability: createStressCapability(stressN, Number.isFinite(moveParam) ? moveParam : undefined),
    });
    await host.activate(STRESS_CAP_ID);
    // PERF READOUT (stress only): count real rAF frames and log the actual render FPS every 2s, so
    // "FPS for a given dot count" is an objective number — a stalled main thread shows as low FPS.
    if (typeof requestAnimationFrame !== "undefined") {
      let frames = 0;
      let last = performance.now();
      const fpsLoop = (): void => {
        frames++;
        const now = performance.now();
        if (now - last >= 2000) {
          // eslint-disable-next-line no-console
          console.log(`[stress-perf] FPS=${Math.round((frames * 1000) / (now - last))} stress=${stressN}`);
          frames = 0;
          last = now;
        }
        requestAnimationFrame(fpsLoop);
      };
      requestAnimationFrame(fpsLoop);
    }
  }

  // --- TRUE OWN-POSITION SHARING + CALL-FOR-EVAC (host/ownPosition.ts + host/evacRequest.ts).
  //     Own position publishes through the SAME bus → pdp.record → projection → sole-host-writer
  //     paint path as every object (I12/I13 — no engine bypass) AND rides the S1-TAK-GATEWAY
  //     /emit egress when armed (VITE_EMIT=1 / ?emit=1) — identical posture to markers/chat.
  //     SECURITY RAILS live in ownPosition.ts (default OFF · double consent · fail-closed on
  //     geolocation error/staleness · OFF is immediate · no coordinates in log lines). ---
  const presenceEmit = makeChatEmit(); // undefined unless egress is armed; fail-soft always
  const publishAndEmit = (env: CanonicalEnvelope): void => {
    // Local publish FIRST and unconditionally (the picture is the source of truth);
    // egress is fire-and-forget + fail-soft (emitClient swallows) — never gates it.
    void bus.publish(env);
    try {
      presenceEmit?.(env);
    } catch {
      /* the egress hook is fail-soft by construction; never let it break a publish */
    }
  };
  const ownPosition = createOwnPositionSharer({
    requestConsent: () => showShareLocationConsentDialog(),
    publish: publishAndEmit,
    identity: () => {
      // The presence callsign = the operator display identity: the (schema-v4)
      // `user.callsign` leaf when present, else the logged-in username. Read with
      // optional chaining so this lane is green before the settings lane lands v4.
      const s = getSettings() as unknown as { user?: { callsign?: unknown } };
      const leaf = typeof s?.user?.callsign === "string" ? s.user.callsign.trim() : "";
      const username = usernameFromClaims(realClaims, identity.label || "operator");
      return { username, callsign: leaf || username };
    },
    updateSeconds: () => readPresenceSettings(getSettings()).updateSeconds,
    surface: (n) => pushToast({ title: n.title, body: n.body, tone: n.tone }),
    onStateChange: (sharing) => {
      status.shareLocation = sharing;
      emitStatus();
    },
  });
  // NOTE the persisted presence.shareLocation preference NEVER auto-starts sharing at
  // boot — consent is per-session (fail-closed). The operator re-enables via ⌘K.

  /** CALL FOR EVAC — resolve the location (own position ONLY while sharing is ON —
   *  ownPosition.currentFix() is null otherwise — else selected point / map center,
   *  STATED in the request), confirm urgency+note in the composer (submitting is the
   *  operator's explicit confirmation of a fallback point), then publish + emit the
   *  kind:"alert" subtype:"evac" envelope. Toast/audit strings carry the SOURCE, never
   *  the coordinates. */
  const callForEvac = async (): Promise<void> => {
    const center = map.getCenter();
    const loc = resolveEvacLocation({
      ownFix: ownPosition.currentFix(),
      selected: status.selected,
      mapCenter: { lng: center.lng, lat: center.lat },
    });
    const form = await showEvacComposerDialog(loc);
    if (form === null) return; // cancelled — nothing is sent
    const env = buildEvacEnvelope(
      { position: loc.position, label: loc.label, source: loc.source },
      { priority: form.priority, ...(form.note ? { note: form.note } : {}), initiator: auditActor },
    );
    publishAndEmit(env);
    pushToast({
      title: "EVAC · REQUEST SENT",
      body: `${form.priority.toUpperCase()} — location: ${loc.source}. Broadcast to the picture${presenceEmit ? " + TAK net" : ""}.`,
      tone: "alert",
    });
  };

  // The LIVE self-location accessor for the C3 evac command (copilotEvac.ts): the real
  // fix while sharing is ON, else the Wave-A placeholder. The command stays human-gated
  // (risk:'irreversible', no confirm hook) so the placeholder is never published live.
  const copilotSelfLocation = (): SelfLocation => {
    const fix = ownPosition.currentFix();
    if (fix) return { position: [fix.lon, fix.lat], label: "OWN POSITION (live)", source: "own-position" };
    return placeholderSelfLocation();
  };

  // THE COP COPILOT CAPABILITY (0.2b, Wave A): takbot folded in as a C3 capability. Registered
  // WITH its CapabilityManifest (contributes panel+command; the irreversible evac is humans-only
  // at the manifest layer) and activated on the SAME host. The factory returns { capability, api }
  // (mirrors createChatCapability): the host owns the capability; the `api` is threaded out via the
  // CopHandle so App.tsx mounts <CopilotPanel api={...} />. The copilot READS the host-filtered
  // (PDP-interposed) bus only — its working set is cleared-visible by construction (Glomar-safe);
  // it never writes the engine (no-bypass-check enforces it). Wave-B live-wiring: docs/COPILOT-WAVE-B-PLAN.md.
  // personaShort: a compact acting-subject tag for the panel header ("ACTING AS {personaShort}").
  // Display only — NOT a security boundary (the host-filtered bus gates the working set). Prefers
  // the persona's REAL display name ("Sarah Chen" → "SARAH CHEN"); fail-soft to the boot label. It
  // reflects the BOOT persona; a later setIdentity re-filters the picture but does not recreate the
  // copilot capability.
  const personaShort = displayNameFor(identity.label || "OPERATOR").toUpperCase();
  const { capability: copilotCapability, api: copilotApi } = createCopilotCapability({
    personaShort,
    // Live-when-sharing self-location (falls back to the Wave-A placeholder; the
    // evac CommandSpec remains human-gated so the placeholder never publishes).
    selfLocation: copilotSelfLocation,
  });
  host.register({ capability: copilotCapability, manifest: COPILOT_MANIFEST });
  await host.activate(COPILOT_CAPABILITY_ID);

  // THE LIVE ADS-B CAPABILITY (0.2b, Wave B): real aircraft over the Hormuz AO via OpenSky (anon
  // REST), mapped to clear-shell `track` envelopes with plane-type bucketing; offline-graceful
  // (bundled snapshot fallback, never throws into the host). Registered WITH its manifest,
  // publish-only (no engine write — no-bypass-check enforces); the host paints the cleared-visible
  // aircraft. Public ADS-B = UNCLASSIFIED. Its T0 proved single-paint (host painted exactly N ids).
  host.register({ capability: createAdsbCapability(), manifest: ADSB_MANIFEST });
  await host.activate(ADSB_CAP_ID);

  // LIVE PUBLIC FEEDS (UNCLASSIFIED, no-auth): USGS earthquakes + NWS active alerts, same
  // contribute-only SDK pattern as ADS-B (publish-only, host paints; offline-graceful snapshot
  // fallback). Real global/US hazard envelopes (kind:"alert") flow through the bus → PDP → paint.
  host.register({ capability: createUsgsCapability(), manifest: USGS_MANIFEST });
  await host.activate(USGS_CAP_ID);
  host.register({ capability: createNwsCapability(), manifest: NWS_MANIFEST });
  await host.activate(NWS_CAP_ID);

  // THE ENCRYPTED CHAT CAPABILITY (ATAK-interop Wave 2): the shared @tak-tdf/cap-chat
  // capability, wired with the COP's I5 chat key-access port + the logged-in persona.
  //   - chatKeyAccess seals every body as a REAL TDF client-side AS the operator (L1); the
  //     room marking is the seal floor (L2). R3 — the LIVE namespace (identity.bundle's, the
  //     /api/config namespace) is passed as expectedNamespace so the mandatory-namespace
  //     fail-closed guard is ARMED in production (a restrictive marking refuses to seal
  //     against an unverified bundle namespace). The bundle getter returns the LIVE bundle
  //     (pdp.bundle()) so the I5 produce-bind and the I4 read-filter decide over IDENTICAL
  //     attributes (lockstep).
  //   - the subject is the logged-in persona (one identity drives I4 + I5).
  //   - the capability subscribes to the PDP-INTERPOSED bus, so a denied room/message never
  //     reaches ingest → the Glomar room list folds STRICTLY off cleared envelopes (§4, C4).
  const liveNamespace = identity.bundle.namespace;
  const chatSubjectId = subjectIdFromClaims(identity.claims, identity.label || "operator");
  const chatKeyAccess = createChatKeyAccess(() => pdp.bundle(), { expectedNamespace: liveNamespace });
  // C5 — PLAINTEXT-BRIDGE AUDIT (§5.6): map the adapter's structured bridge event onto the
  // append-only Seam-B ledger as a PLAINTEXT_BRIDGE verdict. The host OBSERVES the bridge
  // action the adapter took (it never decides it) — a pure side-channel record, identical in
  // discipline to the KAS GRANT/DENY rows. Rule 4 holds (verdicts live ONLY in the Audit pane
  // + Trust modal): recordAudit prepends the bounded ring + emits, no ambient chrome. The
  // SUBJECT names the room + target legacy callsigns (+ message id on a send); the POLICYREF
  // is the room's policy ref; the ACTOR (live persona short code) + Zulu time are stamped by
  // recordAudit. Every ARM / PLAINTEXT_SEND / EXPIRY_NO_SEND is therefore captured.
  const recordBridgeAudit = (ev: BridgeAuditEvent): void => {
    const targets = ev.targetLegacy.length ? ev.targetLegacy.join(",") : "—";
    const tail =
      ev.kind === "PLAINTEXT_SEND" && ev.messageId
        ? ` · msg ${ev.messageId}`
        : ev.kind === "EXPIRY_NO_SEND" && ev.reason
          ? ` · ${ev.reason}`
          : "";
    const subject = `BRIDGE ${ev.kind} · ${ev.room} → [${targets}]${tail}`;
    recordAudit("PLAINTEXT_BRIDGE", subject, compactPolicyRef(ev.policyRef));
  };
  // DEMO-TIER PER-MESSAGE SIGNER: a per-session WebCrypto signer minted async at boot. Until it
  // resolves (or if WebCrypto/keygen fails), the getter returns undefined → messages send UNSIGNED
  // ("asserted, not signed" — the honest current state), never blocking a send. New session = new
  // signer (a fresh startCop for a new identity mints a fresh one). Display/trust-only, client-
  // asserted; the FUTURE registered-key home is idplite delegation keygen (RegisterDelegationPubkey).
  let chatSigner: MessageSigner | undefined;
  void createSessionSigner()
    .then((s) => { chatSigner = s; })
    .catch((e) => console.warn("[chat] session signer unavailable — messages send unsigned (asserted):", e));

  const chat = createChatCapability({
    keyAccess: chatKeyAccess,
    subject: { id: chatSubjectId, ...identity.claims },
    recordBridgeAudit,
    // DEMO-TIER MESSAGE SIGNER (live getter; undefined until the async keypair is ready → the
    // message is simply unsigned). Signs each send over the SEAL + id|room|sender (Guardrail 1).
    signer: () => chatSigner,
    // DISPLAY CALLSIGN (live getter — the settings leaf can change without a re-login):
    // sends stamp senderCallsign on the clear shell + presence carries it, so peers render
    // "TRINITY-99" instead of the raw login id. Same fold as the presence identity.
    callsign: () => {
      const s = getSettings() as unknown as { user?: { callsign?: unknown } };
      const leaf = typeof s?.user?.callsign === "string" ? s.user.callsign.trim() : "";
      return leaf || undefined;
    },
    // HONEST SESSION STATE (injected so the adapter's static graph stays kas-free for the
    // node-side tsx checks): the panel renders an expired-session banner instead of letting
    // a KAS 401 masquerade as clearance denials, and sealed rows auto-recover when the
    // silent refresh adopts a fresh token.
    authState: tokenState,
    canRenew: canSilentlyRenew,
    onTokenRenewed,
  });
  const chatApi: ChatApi = chat.api;
  // Global chat unread → status snapshot (drives the FunctionRail chat badge). totalUnread
  // folds off the SAME post-PDP-seam store as rooms(), so a denied room can never surface a
  // badge (Glomar). Change-guarded so we only emit when the count actually moves.
  chatApi.subscribe(() => {
    const n = chatApi.totalUnread();
    if (status.chatUnread !== n) {
      status.chatUnread = n;
      emitStatus();
    }
  });
  host.register({ capability: chat.capability });
  await host.activate(CHAT_CAPABILITY_ID);

  // H2 — wire the down-filter chat rebuild (declared above applyViewScope; assigned now that
  // chat + chatKeyAccess exist). The down-filter is the SAME persona viewing LOWER, so the JWT
  // (and setKasToken's cache-clear) never fires — we MUST clear the chatKeyAccess plaintext
  // cache + rebuild the chat store over the DOWN-visible census ourselves, or a body decoded at
  // full clearance survives in the roomStore (redacted=false) + leaks via lastMessagePreview in
  // the lower view. switchSubject (called just before this in applyViewScope) re-gated the
  // projection to the down level, so projection.censusBasis() is the DOWN-visible set; a SECRET
  // room/message absent at the down level is ABSENT from the rebuild → its preview/body is gone.
  // On RESTORE (un-down-filter) this runs again at the higher level → the body returns iff the
  // higher level re-permits it. Same single membership authority as the map/banner fold.
  rebuildChatForDownView = (claimsForView: SubjectClaims): void => {
    chatKeyAccess.clearPlaintextCache();
    const viewSubjectId = subjectIdFromClaims(claimsForView, identity.label || "operator");
    const visibleChatEnvelopes = projection.censusBasis().filter(isChatEnvelope);
    void chat
      .rebuildForSubject({ id: viewSubjectId, ...claimsForView }, visibleChatEnvelopes)
      .catch((e) => console.error("[cop] chat room-store rebuild on down-filter failed:", e));
  };

  // Seam B: the boot capability set is now activated, so its PanelSpec contributions are
  // in the registry. Fire the panel change-signal so a subscribed <PanelHost> re-reads
  // host.panels() and mounts any add-in panel registered above (a third-party add-in
  // activated later fires this again from its own activation site).
  emitPanelsChanged();

  // (The self-scope RE-ASSERT backstop is GONE — Increment 3. It existed only because the
  // capabilities painted raw and NOT self-scope-aware, and could re-show a hidden/removed/
  // filtered member on a feed re-emit. Now the HOST's per-publish painter is the SOLE painter
  // and it goes through paintRow — which applies the operator self-scope — so a self-scoped-out
  // member is never re-shown in the first place. One fewer bus subscriber, no ordering race.)

  // --- The single rAF render loop (imperative; React is not here) ---
  let raf = 0;
  let lastTs = performance.now();
  const frame = (ts: number): void => {
    const dt = ts - lastTs;
    lastTs = ts;
    engine.tick(dt);
    raf = requestAnimationFrame(frame);
  };
  raf = requestAnimationFrame(frame);

  // REFOCUS SNAP: a hidden tab PAUSES the rAF loop above while the WS feed keeps upserting, so
  // the first writeFrame after refocus measures interval = the whole hidden period — that
  // poisons transitionMs for one frame (clamped to its 4.5s max) and every dot GLIDES seconds
  // across the accumulated jump. Reuse the down-filter re-gate mechanism: flag the backend so
  // the refocus data frame SNAPS its positions (transitions disabled for that one frame); the
  // motion glide resumes on the following frame. Guarded for headless/test contexts (no DOM).
  let onVisibilityRefocus: (() => void) | null = null;
  if (typeof document !== "undefined") {
    onVisibilityRefocus = (): void => {
      if (document.visibilityState === "visible") backend.notifyMembershipChange();
    };
    document.addEventListener("visibilitychange", onVisibilityRefocus);
  }

  // --- MARKER-AUTHOR (S3): the operator drops a typed waypoint on the map. stageAction({kind:
  //     "waypoint"}) ARMS placement; the NEXT non-shift map click drops the marker at that
  //     lngLat instead of picking. The envelope is minted by the markerCapability builder (the
  //     fail-closed classification gate lives there), published through the SAME bus →
  //     pdp.record → projection → sole-host-writer paint path every object rides (I12/I13 — no
  //     engine bypass), AND handed to the S1-TAK-GATEWAY egress hook (/emit) when armed. ---
  let markerPlacementArmed = false;
  let markerSeq = 0;
  // The outbound CoT egress hook (S1-TAK-GATEWAY) — same client cap-chat uses. `undefined` unless
  // egress is ARMED (VITE_EMIT=1 / ?emit=1); a throwing hook is swallowed (the local publish
  // always stands). Reused here so an authored marker crosses to the TAK net like a chat message.
  const markerEmit = makeChatEmit();
  const setMarkerPlacement = (armed: boolean): void => {
    if (markerPlacementArmed === armed) return;
    markerPlacementArmed = armed;
    // Cursor affordance: crosshair while armed so the operator sees they are placing, not picking.
    try {
      map.getCanvas().style.cursor = armed ? "crosshair" : "";
    } catch {
      // headless / no canvas — placement still works via the click handler.
    }
  };
  /** Drop ONE operator-authored waypoint at a map lngLat. FAIL-CLOSED on the classification: the
   *  marking is the operator's CURRENT working pick (the down-filter/query level); if it does not
   *  resolve to a known level we REFUSE (toast + audit DENY) and never publish a mismarked object. */
  const dropMarkerAt = (lon: number, lat: number): void => {
    // The operator's CURRENT pick = the working/query level they are operating at. Fail-closed:
    // resolveMarkerClassification returns null for an absent/unknown level ⇒ we refuse.
    const cls = resolveMarkerClassification(queryLevel);
    if (cls === null) {
      pushToast({
        title: "MARKER · REFUSED",
        body: "No valid classification selected — set a working level before placing a waypoint.",
        tone: "alert",
      });
      recordAudit("DENY", "operator waypoint · geometry", "MARKER_CLASSIFICATION_UNSET");
      return;
    }
    markerSeq += 1;
    const nowIso = new Date().toISOString();
    // 6-hour validity window so the authored point ages out of the time-window like any object.
    const staleIso = new Date(Date.now() + 6 * 3_600_000).toISOString();
    let env: CanonicalEnvelope;
    try {
      env = buildMarkerEnvelope({
        lon,
        lat,
        classification: cls,
        label: `WP-${markerSeq}`,
        authorShort: auditActor,
        nowIso,
        staleIso,
        id: `cop:marker:${auditActor}:${markerSeq}:${Date.now()}`,
      });
    } catch (err) {
      // The builder's last-gate fail-closed throw — refuse, never paint a mismarked object.
      const reason = err instanceof MarkerClassificationError ? "MARKER_CLASSIFICATION_INVALID" : "MARKER_BUILD_FAILED";
      pushToast({ title: "MARKER · REFUSED", body: (err as Error).message, tone: "alert" });
      recordAudit("DENY", "operator waypoint · geometry", reason);
      return;
    }
    // PUBLISH through the proper host seam (bus → pdp.record → projection delta → sole-host-writer
    // paint). Identical to HostContext.data.publish (cop.ts data.publish = bus.publish); NO engine
    // bypass, so the authored marker is gated + painted projection-coherently (I12/I13).
    void bus.publish(env);
    // EGRESS (S1-TAK-GATEWAY): hand the same envelope to /emit when armed. Fire-and-forget +
    // fail-soft by construction (the local publish above already stands). Absent hook ⇒ no egress.
    try {
      markerEmit?.(env);
    } catch {
      // belt-and-suspenders: the egress hook is fail-soft, but never let it break placement.
    }
    pushToast({ title: "MARKER · PLACED", body: `WP-${markerSeq} @ ${cls}`, tone: "success" });
  };

  // Picking: map click → engine.pick → HUD selection (off the render loop). When marker placement
  // is ARMED, the next non-shift click DROPS a waypoint instead of picking, then disarms.
  const onClick = (e: maplibregl.MapMouseEvent): void => {
    // Shift is the 2.5D tilt-gesture modifier — a Shift+click must NOT pick-select OR place.
    if (e.originalEvent.shiftKey) return;
    if (markerPlacementArmed) {
      dropMarkerAt(e.lngLat.lng, e.lngLat.lat);
      setMarkerPlacement(false); // one click = one waypoint; re-arm via the rail button.
      return;
    }
    engine.pick({ x: e.point.x, y: e.point.y });
  };
  map.on("click", onClick);

  // CURSOR READOUT (B1, §2.8): a THROTTLED mousemove → lon/lat/MGRS for the
  // Cartouche, emitted OFF the rAF render loop (MapLibre hands us lngLat directly).
  // Throttled to ~16/s so a fast sweep never floods React with status snapshots.
  let lastCursorEmit = 0;
  const onMouseMove = (e: maplibregl.MapMouseEvent): void => {
    const now = performance.now();
    if (now - lastCursorEmit < 60) return;
    lastCursorEmit = now;
    backend.setHovered(backend.pickId({ x: e.point.x, y: e.point.y })); // bloom the hovered track's smear + leader + stalk (focus-only render)
    const lon = e.lngLat.lng;
    const lat = e.lngLat.lat;
    status.cursor = { lon, lat, mgrs: toMgrs(lon, lat) };
    emitStatus();
  };
  const onMouseOut = (): void => {
    backend.setHovered(null); // clear the hover bloom when the cursor leaves the map
    if (status.cursor === null) return;
    status.cursor = null;
    emitStatus();
  };
  map.on("mousemove", onMouseMove);
  map.on("mouseout", onMouseOut);

  // 2.5D-DEPTH: keep status.viewMode in sync with the LIVE camera — the operator can tilt by hand
  // (right-drag / two-finger / compass-reset) outside setViewMode. pitchend fires after those
  // gestures AND the programmatic easeTo; >5deg reads as tilted 2.5D, else the flat 2D scope.
  const onPitchEnd = (): void => {
    const mode: "2d" | "2.5d" = map.getPitch() > 5 ? "2.5d" : "2d";
    if (status.viewMode !== mode) {
      status.viewMode = mode;
      emitStatus();
    }
  };
  map.on("pitchend", onPitchEnd);

  // 2.5D-DEPTH: Shift + left-drag → Google-Earth tilt/orbit (vertical drag = pitch, horizontal =
  // bearing). POINTER EVENTS + setPointerCapture so the gesture ALWAYS ends — pointerup/pointercancel
  // fire even if the button is released off-window (a raw window-mouseup would leak, sticking the
  // map with dragPan disabled). Listeners live on the CANVAS, so they die with it on dispose (no
  // window-listener leak). Needs boxZoom:false (set in the constructor).
  {
    const TILT_SENSITIVITY = 0.4; // deg of pitch per px of vertical drag
    const ORBIT_SENSITIVITY = 0.35; // deg of bearing per px of horizontal drag
    const canvas = map.getCanvas();
    let tilting = false;
    let lastX = 0;
    let lastY = 0;
    const endTilt = (): void => {
      if (!tilting) return;
      tilting = false;
      canvas.style.cursor = "";
      map.dragPan.enable();
      onPitchEnd(); // jumpTo doesn't fire pitchend — sync status.viewMode to the new tilt
    };
    const onPointerMove = (e: PointerEvent): void => {
      if (!tilting) return;
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      lastX = e.clientX;
      lastY = e.clientY;
      // Drag UP (dy<0) → raise pitch; clamp to [0, 85]. Horizontal → orbit bearing.
      const nextPitch = Math.max(0, Math.min(85, map.getPitch() - dy * TILT_SENSITIVITY));
      map.jumpTo({ pitch: nextPitch, bearing: map.getBearing() + dx * ORBIT_SENSITIVITY });
    };
    const onPointerDown = (e: PointerEvent): void => {
      if (!e.shiftKey || e.button !== 0) return; // Shift + LEFT button only
      e.preventDefault();
      tilting = true;
      lastX = e.clientX;
      lastY = e.clientY;
      canvas.style.cursor = "ns-resize";
      map.dragPan.disable(); // don't also pan while shift-tilting
      try {
        canvas.setPointerCapture(e.pointerId); // guarantees pointerup/cancel even off-window
      } catch {
        // setPointerCapture unsupported on this engine — canvas pointerup still ends most gestures
      }
    };
    canvas.addEventListener("pointerdown", onPointerDown);
    canvas.addEventListener("pointermove", onPointerMove);
    canvas.addEventListener("pointerup", endTilt);
    canvas.addEventListener("pointercancel", endTilt);
  }

  // --- Feed (live WS / mock) publishing onto the bus ---
  let feed: FeedHandle | null = null;
  feed = startFeed(bus, (mode) => {
    status.feedMode = mode;
    // The edition cartouche names the connectivity tier off the feed mode, so a
    // link change (connecting → live/mock) re-derives the named edition + readout.
    recomputeShell();
  });

  // --- REAL crypto: publish the seeded, genuinely-TDF-protected contacts (minted on
  //     defense.local by scripts/seed-cop-tdf.mjs). Each flows through the SAME bus →
  //     PDP filter → render path as every other object; clicking one rewraps it
  //     client-side against the live KAS AS the logged-in persona (grant → decrypt
  //     in-place, deny → stays sealed). Degrades silently if not seeded.
  //
  //     The seeded contacts are HORMUZ-relocated TDFs (they belong to the default
  //     theater), so a live theater switch CLEARS them (clearPicture) and switching BACK
  //     to hormuz RE-PUBLISHES them. We cache the loaded objects on first fetch so the
  //     re-seed is a pure re-publish (no re-fetch). publishSeededObjects() is the single
  //     re-usable emit path (boot + switch-to-hormuz).
  let seededObjects: SeededObject[] = [];
  const publishSeededObjects = (): void => {
    for (const o of seededObjects) void bus.publish(realKasTrack(o));
  };
  void loadSeededObjects().then((objs) => {
    seededObjects = objs;
    publishSeededObjects();
  });

  // The active theater the COP booted into (the ?scenario= selection, default hormuz). The
  // layers-pane radio highlights this; setScenario swaps it live. Set before the first emit.
  status.activeScenario = activeScenarioId();

  // --- LIVE THEATER SWITCH support (TODO#scenario-switch-ui) ---------------------------
  // CLEAR THE WHOLE LIVE PICTURE — the host side of a theater swap. The feed stops the old
  // scenario's publisher FIRST (so no old-theater tick races this), then calls this. We:
  //   1. enumerate EVERY recorded id (pdp.registryValues — the full registry, a SUPERSET of
  //      the painted set) and REMOVE them from the engine via the host's SOLE-WRITER remove
  //      path (applyDeltaToEngine) — never a parallel engine write, so the no-bypass thesis
  //      holds and there is one clear-the-engine path.
  //   2. WIPE the derived-state stores (pdp + projection) in LOCKSTEP via their clear(), so a
  //      later re-filter / reproject can NEVER resurrect the old theater (the registry +
  //      projection.recorded only grow otherwise — there was no clear path before this).
  //   3. reset the operator SELF-SCOPE + per-theater UI state that keys off the old ids
  //      (layer hide/remove/opacity, the active filter, the alert log, the selection, the
  //      copilot working set) so none of it bleeds onto the new theater.
  // The new theater then republishes onto the SAME bus → pdp.record + projection.onRecord →
  // the delta consumer repaints + re-folds the banner/census — all clearance-safe (no bypass).
  const clearPicture = (): void => {
    const ids = pdp.registryValues().map((e) => e.id);
    if (ids.length > 0) {
      applyDeltaToEngine({ adds: [], removes: ids, replaces: [], epoch: -1 });
    }
    // Wipe the canonical + projected stores together (projection.clear() also clears its
    // wrapped PDP) so the dual-authority coherence pair stays equal (both empty).
    pdp.clear();
    projection.clear();
    // IVM Increment 2: projection.clear() wipes the rows WITHOUT emitting a delta (the host already
    // removed the ids above), so the incremental histogram would keep the old theater's counts —
    // reset it in lockstep here (the recomputeBanner/recomputeShell at the tail then read empty).
    resetHistogram();
    // Operator self-scope + per-theater derived state keyed off the (now-gone) old ids.
    layerHidden.clear();
    layerOpacity.clear();
    layerRemoved.clear();
    filterPredicate = null;
    status.activeFilter = null;
    // Theater switch also wipes the PERSISTED ack arrays for this user — the old theater's
    // ids must not linger in their settings blob (alertAcks.wipe persists empty arrays).
    alertAcks.wipe();
    alertFirstSeenZulu.clear();
    lastAlertVisible = [];
    status.alertLog = [];
    status.alertCount = 0;
    status.trackCount = 0;
    // Drop any selection (and its decrypt state) — the selected entity is being removed.
    if (status.selected) onSelect(null);
    // The copilot ingests track/observation/alert kinds — purge its working set so the old
    // theater's rows can't answer questions against the new one (chat is authored, not
    // theater data — scenarios publish no chat envelopes, so the room store needs no wipe).
    copilotApi.reset();
    // Fold the now-empty projection into the banner/census so the UI reads empty for the
    // beat between clear and the new theater's first publish.
    recomputeBanner();
    recomputeShell();
    emitStatus();
  };

  // Recenter the camera on the ACTIVE picture's extent — data-driven (no per-theater
  // hardcoded coords), so it fits hormuz/taiwan/levant AND the worldwide global-intel set.
  // Reads the production pdp's visible set (LIVE geometry, projection-coherent). Falls back
  // to INITIAL_VIEW when nothing is visible (e.g. a fully down-filtered persona).
  const recenterOnVisibleExtent = (): void => {
    let minLon = Infinity, minLat = Infinity, maxLon = -Infinity, maxLat = -Infinity, n = 0;
    for (const env of pdp.visibleEnvelopes()) {
      const g = env.geometry;
      if (!g || g.type !== "Point") continue;
      const c = g.coordinates as number[];
      if (!Array.isArray(c) || c.length < 2) continue;
      const lon = c[0];
      const lat = c[1];
      if (!Number.isFinite(lon) || !Number.isFinite(lat)) continue;
      minLon = Math.min(minLon, lon); maxLon = Math.max(maxLon, lon);
      minLat = Math.min(minLat, lat); maxLat = Math.max(maxLat, lat);
      n++;
    }
    if (n === 0) {
      engine.setView(INITIAL_VIEW);
      return;
    }
    const longitude = (minLon + maxLon) / 2;
    const latitude = (minLat + maxLat) / 2;
    // Rough fit zoom from the wider lon/lat span: a ~0.7° regional spread → ~z8, a global
    // spread → ~z1. Clamped to the demonstrator's comfortable band [1, 8].
    const span = Math.max(maxLon - minLon, maxLat - minLat, 0.01);
    const zoom = Math.max(1, Math.min(8, Math.round(Math.log2(360 / span)) - 1));
    engine.setView({ longitude, latitude, zoom });
  };

  recomputeQueryScope(); // seed the down-filter range from the booting persona
  emitStatus();

  // AUTO-WIDEN once the initial picture has settled (the live feed + seeded objects arrive
  // async). If "last 30 min" leaves the map too sparse, step the recency width up until ≥20
  // contacts or all-time — a one-time "show enough context" nudge the operator can override.
  window.setTimeout(maybeAutoWiden, 2_500);

  // GLOMAR-SAFE id lookup: resolve an id ONLY against the cleared-visible set, so a
  // shell action (locate/select/zoom) can never touch — or confirm the existence of — a
  // withheld object. Returns null for a hidden or unknown id, identically. Reads the
  // production pdp's visible set for LIVE geometry (membership is projection-coherent):
  // locate/flyTo needs the object's CURRENT position, and the projection's row geometry is
  // frozen on the firehose (it would fly to the object's origin, not where it is now).
  const findVisible = (id: string): CanonicalEnvelope | null => {
    for (const env of pdp.visibleEnvelopes()) if (env.id === id) return env;
    return null;
  };

  // Fly the camera to a visible entity's point (held under map-primacy). Used by
  // locate / zoomToLayer. No-op for non-point geometry or a hidden/unknown id.
  const flyTo = (env: CanonicalEnvelope | null): void => {
    const g = env?.geometry;
    if (!g || g.type !== "Point") return;
    const c = g.coordinates as [number, number];
    if (!Array.isArray(c) || c.length < 2) return;
    engine.setView({ longitude: c[0], latitude: c[1], zoom: Math.max(INITIAL_VIEW.zoom, 10) });
  };

  // COLLAPSE CONTRACT polled-nudge schedule (§4.3, B3). A CSS-grid grow can take a
  // couple frames to settle, and a single map.resize() can read the OLD box. So
  // after any explicit collapse / layout toggle we re-fit on a short schedule
  // [0, 32, 80, 160]ms (graybox scheduleResizeDeck parity). The camera is held by
  // map.resize(), so every extra fit is idempotent and never slides a track.
  const NUDGE_MS = [0, 32, 80, 160];
  const scheduleRefit = (): void => {
    for (const ms of NUDGE_MS) {
      if (ms === 0) map.resize();
      else window.setTimeout(() => map.resize(), ms);
    }
  };

  return {
    // The copilot panel-facing API (threaded out so App.tsx mounts <CopilotPanel api={...} />).
    copilotApi,
    // The encrypted-chat panel-facing API (Wave-3 mounts <ChatPanel api={...} /> off this).
    chatApi,
    async dispose(): Promise<void> {
      cancelAnimationFrame(raf);
      // Remove the refocus-snap listener with the rAF loop it compensates for (non-null only
      // when a DOM existed at boot, so document is present here by construction).
      if (onVisibilityRefocus) document.removeEventListener("visibilitychange", onVisibilityRefocus);
      window.clearInterval(ageOutTimer);
      // Stop TRUE-LOCATION sharing with the host (clears the geolocation watch +
      // publish timer + drops the captured fix — nothing emits after teardown).
      ownPosition.dispose();
      stopKasHeartbeat();
      feed?.stop();
      unsubTracks();
      unsubRenderSettings();
      unsubAlerts();
      unsubAckPersona();
      // COPILOT EPHEMERALITY — on teardown/logout leave NO copilot state behind: purge the thread
      // + working set BEFORE deactivating the capability (deactivate() drops the host-filtered
      // subscription but does not itself clear the conversation or the read-side rows).
      copilotApi.reset();
      await host.deactivate("scop:cap:tracks");
      await host.deactivate(DENSITY_CAP_ID);
      await host.deactivate(COPILOT_CAPABILITY_ID);
      await host.deactivate(ADSB_CAP_ID);
      await host.deactivate(USGS_CAP_ID);
      await host.deactivate(NWS_CAP_ID);
      await host.deactivate(CHAT_CAPABILITY_ID);
      chatKeyAccess.dispose(); // unregister the identity-switch listener + wipe the plaintext cache
      map.off("click", onClick);
      map.off("mousemove", onMouseMove);
      map.off("mouseout", onMouseOut);
      map.off("pitchend", onPitchEnd); // map-level listener (line ~1830) — must be removed on unmount
      engine.dispose();
      map.remove();
      statusSubs.clear();
      graphSubs.clear();
      panelSubs.clear();
    },
    onStatus(cb): () => void {
      statusSubs.add(cb);
      cb({ ...status });
      return () => statusSubs.delete(cb);
    },
    onGraph(cb): () => void {
      graphSubs.add(cb);
      return () => graphSubs.delete(cb);
    },
    panels(): PanelContribution[] {
      // Narrow read-through to the host's cross-capability enumerator — the ONLY host
      // surface App.tsx sees for panel mounting (the host itself is never exposed).
      return host.panels();
    },
    onPanelsChanged(cb): () => void {
      panelSubs.add(cb);
      cb(); // invoke once with the current set so a late subscriber never misses boot panels
      return () => panelSubs.delete(cb);
    },
    setIdentity(claims: SubjectClaims, token: string): void {
      // The new persona's real claims become the down-filter ceiling; reset the query level to their
      // FULL clearance AND clear any view-as overlay (a fresh login opens at your own full view — the
      // prior persona's descope must not carry over). switchSubject(claims) below drives the picture
      // with the full new claims (no overlay).
      realClaims = claims;
      queryLevel = clearanceLevelOf(claims);
      viewOverlay = null;
      // COPILOT EPHEMERALITY — a persona switch is a clean break: purge the copilot thread +
      // working set so the PRIOR persona's Q&A turns/rows never bleed into the new operator's view
      // (mirrors the chat room-store rebuild below). The host-filtered subscription re-ingests the
      // new subject's visible census after switchSubject. Re-seed the narrowing tracker to the new
      // persona's FULL claims so the next applyViewScope measures narrowings from THEIR full view.
      copilotApi.reset();
      lastEffectiveClaims = claims;
      // The Audit ledger's actor tag follows the live persona (display only — the spine
      // gates the real verdicts). Re-derive the short code from the new claims.
      auditActor = shortCodeFromClaims(claims, auditActor);
      // The new persona drives I5 too: subsequent KAS opens rewrap AS this token.
      setKasToken(token);
      // Re-filter the WHOLE picture for the new identity: switchSubject sets the
      // production pdp, then the projection's onSubject delta re-gates the engine
      // (objects now permitted appear, objects now above clearance vanish — existence not
      // revealed) + folds the banner/census, then re-asserts the operator self-scope.
      switchSubject(claims);
      // R2 — CHAT ROOM-STORE REBUILD ON IDENTITY SWITCH (§4.3). The plaintext cache was
      // already cleared synchronously by setKasToken (so no opened body survives). Now
      // rebuild the chat store from SCRATCH over ONLY the new subject's currently-visible
      // (post-PDP-seam) chat envelopes — switchSubject just re-gated the projection, so
      // projection.censusBasis() is the new subject's visible set. A room the lower persona
      // can't see is ABSENT from that set → it VANISHES from the room list; a HIGH body
      // decoded under HIGH is gone (the store is rebuilt, and a re-ingested HIGH envelope
      // re-decides via a fresh rewrap → redacted for LOW). Fire-and-forget with isolation:
      // this is a UI-store refresh, not a security gate (the seam + cache clear are the gate).
      const nextSubjectId = subjectIdFromClaims(claims, identity.label || "operator");
      const visibleChatEnvelopes = projection.censusBasis().filter(isChatEnvelope);
      void chat
        .rebuildForSubject({ id: nextSubjectId, ...claims }, visibleChatEnvelopes)
        .catch((e) => console.error("[cop] chat room-store rebuild on identity switch failed:", e));
      recomputeQueryScope();
      // A fresh persona opens at the default recency (30 min) + re-runs the auto-widen
      // heuristic (the new clearance changes the contact count). Resets the manual flag so
      // the new persona gets the "show enough context" default, exactly like a fresh login.
      timeStopIdx = DEFAULT_TIME_IDX;
      timeManual = false;
      timeAnchorMs = null;
      applyTimeWindow();
      maybeAutoWiden();
      recomputeTimeWindow();
      // If the current selection is now hidden, drop it; otherwise re-evaluate it.
      const sel = status.selected;
      if (sel && !pdp.evaluate(sel).presentation.visible) {
        onSelect(null);
      } else {
        onSelect(sel);
      }
      emitStatus();
      assertCoherence("setIdentity");
    },
    setQueryLevel(level: string): void {
      // THIN WRAPPER over the multi-dim overlay: the banner dropdown just writes the classification
      // axis. applyViewOverlay merges it in (any OTHER axes an active view-as narrowed are kept) and
      // re-applies; applyViewScope owns the clamp (actor cap / real clearance) + the copilot wipe +
      // toast on a narrowing. So the legacy dropdown and the view-as panel share ONE code path.
      if (!(level in CLASS_ORDER)) return;
      applyViewOverlay({ classification: [level] });
    },
    setViewOverlay(spec: DescopeSpec | null): void {
      // Multi-dim view-as entry point. Merge-or-clear the overlay, then re-resolve the picture.
      applyViewOverlay(spec);
    },
    stepTimeWindow(dir: number): void {
      const next = Math.max(0, Math.min(TIME_STOPS.length - 1, timeStopIdx + (dir < 0 ? -1 : 1)));
      if (next === timeStopIdx) return;
      timeStopIdx = next;
      timeManual = true; // the operator owns the width now — auto-widen stops
      applyTimeWindow();
      recomputeTimeWindow();
      emitStatus();
    },
    setTimeWindowIndex(index: number): void {
      const next = Math.max(0, Math.min(TIME_STOPS.length - 1, Math.floor(index)));
      timeStopIdx = next;
      timeManual = true;
      applyTimeWindow();
      recomputeTimeWindow();
      emitStatus();
    },
    goLive(): void {
      if (timeAnchorMs === null) return;
      timeAnchorMs = null;
      applyTimeWindow();
      recomputeTimeWindow();
      emitStatus();
    },
    clearSelection(): void {
      onSelect(null);
    },
    retryDecrypt(): void {
      const env = status.selected;
      if (!env || status.decrypt.state !== "unavailable") return;
      const ref = realKasRef(env);
      if (ref) runDecrypt(env, ref);
    },
    recenter(): void {
      engine.setView(INITIAL_VIEW);
    },
    setMarkerMode(mode: "milsym" | "dot" | "coalition"): void {
      backend.setMarkerMode(mode);
      // Reflect the active carrier in the snapshot so the SettingsDrawer Symbology
      // radio tracks the live choice (the backend swaps the layer; this is the seam
      // that tells the UI which one is on).
      status.markerMode = mode;
      emitStatus();
    },
    setRenderProfile(themeId: string): void {
      const next = THEMES[themeId];
      if (!next) return;
      applyTheme(next);
      backend.setAffiliationColors(affiliationColorsFor(next));
      // Swap the MapLibre basemap to match the profile (Positron for the light/day theme,
      // dark-matter otherwise). Only setStyle when the URL actually changes — setStyle reloads
      // the whole style + refetches tiles. The deck overlay (a control) survives it, but the
      // terrain-dem source does NOT, so re-install terrain once the new style loads.
      const nextBasemap = basemapUrlForProfile(themeId);
      if (nextBasemap !== activeBasemapUrl) {
        activeBasemapUrl = nextBasemap;
        map.setStyle(nextBasemap);
        map.once("style.load", installTerrainForStyle);
      }
      status.renderProfile = themeId;
      emitStatus();
    },
    setViewMode(mode: "2d" | "2.5d"): void {
      // Pure camera move: animate pitch only (center/zoom held). The deck MapboxOverlay
      // rides MapLibre's camera, so air/UAS tracks lift into their altitude as it tilts.
      const pitch = mode === "2.5d" ? TILT_PITCH_DEG : 0;
      map.easeTo({ pitch, duration: pitch > 0 ? 800 : 600, essential: true });
      status.viewMode = mode;
      emitStatus();
    },

    scenarios(): Array<{ id: string; label: string }> {
      // Static registry read for the layers-pane theater list. Fresh array each call so a
      // consumer can't mutate the registry view.
      return scenarioList();
    },
    setScenario(id: string): void {
      // Delegate the scenario LIFECYCLE (stop old → start new on the bus) to the feed, which
      // owns the scenario handle; the host owns the PICTURE, so the feed calls clearPicture
      // BETWEEN stop and start (the load-bearing order: no old-theater tick can race the
      // wipe, and the new theater starts onto a clean engine/PDP/projection). Off-mock the
      // feed leaves setScenario undefined → this is a no-op (theaters only exist in mock).
      const applied = feed?.setScenario?.(id, clearPicture) ?? null;
      if (applied === null) return; // unknown id, already-active, or live mode — nothing changed
      status.activeScenario = applied;
      // The HORMUZ-relocated real-KAS TDFs belong to the default theater and were wiped by
      // clearPicture — re-publish them (from cache) when returning to hormuz so the real-crypto
      // demo survives a round-trip. (No-op cache is empty if seeding never loaded.)
      if (applied === DEFAULT_SCENARIO_ID) publishSeededObjects();
      // The new theater republished synchronously (startTheaterScenario emits once on start),
      // so the engine/PDP/projection already hold it — recenter on its extent + re-derive the
      // down-filter range, then emit. (recomputeBanner/Shell already fired via the delta
      // consumer during the republish; this emit carries the new activeScenario highlight.)
      recenterOnVisibleExtent();
      recomputeQueryScope();
      emitStatus();
    },

    // --- 0.1b "Clean Shell" host-context contract (GROUP 0 seam) ---

    refitMap(): void {
      // COLLAPSE CONTRACT (REAL): re-fit MapLibre's WebGL2 drawing buffer to the new
      // CSS box, camera HELD so tracks do NOT slide under glass. map.resize() re-fits
      // the canvas to the container × dpr; the deck MapboxOverlay rides the same
      // context. The camera (center/zoom) is untouched. Spec §4.
      //
      // We run the [0,32,80,160]ms nudge schedule, not a single resize: a CSS-grid
      // grow can take a couple frames to settle, and a single fit can read the OLD
      // box. Every fit is idempotent (camera held), so the extra fits are free and
      // they guarantee the buffer matches the FINAL box after a collapse/expand. The
      // GutterFrame's ResizeObserver also calls this on the authoritative settle.
      scheduleRefit();
    },

    // --- URL STATE CAPTURE (host/urlState.ts) — presentation/navigation only. ---
    getCamera(): { lat: number; lng: number; zoom: number } | null {
      // Guarded for headless/SSR (getCenter/getZoom absent). MapLibre owns the camera;
      // this is a pure read for reflecting the view into the URL hash.
      const c = map.getCenter?.();
      const z = map.getZoom?.();
      if (!c || typeof z !== "number") return null;
      return { lat: c.lat, lng: c.lng, zoom: z };
    },
    setCamera(cam: { lat: number; lng: number; zoom: number }): void {
      // JUMP (no animation): engine.setView → backend.setView → map.jumpTo. Restoring a
      // shared link should land INSTANTLY on the view, not fly. Center/zoom only — pitch
      // (2.5D) is restored separately via setViewMode. Never re-gates the picture.
      engine.setView({ longitude: cam.lng, latitude: cam.lat, zoom: cam.zoom });
    },
    onMoveEnd(cb: () => void): () => void {
      // MapLibre "moveend" fires after the camera settles (pan/zoom/pitch/jump). The
      // URL-state controller debounces this into a replaceState. Guarded for headless.
      map.on?.("moveend", cb);
      return () => map.off?.("moveend", cb);
    },

    locate(id: string): void {
      // GO rung: fly to a VISIBLE entity (Glomar-safe lookup). Halo follows in GROUP B.
      flyTo(findVisible(id));
    },

    select(id: string): void {
      // SELECT: drive the Ledger re-skin via the existing selection path (which also
      // triggers the KAS rewrap for protected contacts). Visible-only; a hidden/unknown
      // id resolves to null and clears selection — identical to a non-existent object.
      onSelect(findVisible(id));
    },

    setLayerVisible(id: string, visible: boolean): void {
      // Real per-layer show/hide: record the override, drive the engine gate (the
      // group's members are removed/re-shown), and re-derive the spine. A hidden
      // layer is a self-scope, NOT a denial — its members stay in the PDP registry
      // and re-appear when re-shown (Glomar-safe; the census denominator is unchanged).
      if (visible) layerHidden.delete(id);
      else layerHidden.add(id);
      applyLayerGate();
      rescanHiddenByFilter(); // layer state changed with no delta → refresh the fade counter O(N)
      recomputeShell();
    },

    setLayerOpacity(id: string, opacity: number): void {
      // Record the per-layer opacity override (drives the spine row + deck layer
      // props as the renderer reads it). Clamped 0..1; 1 clears the override.
      const clamped = Math.min(1, Math.max(0, opacity));
      if (clamped >= 1) layerOpacity.delete(id);
      else layerOpacity.set(id, clamped);
      // The opacity override is reflected in the spine row immediately. Applying it
      // to the deck layer props (a per-layer alpha multiply) is a renderer concern
      // tracked for the layer-registry pass; the snapshot is honest meanwhile.
      // TRACKED-TASK:TODO.md#shell-composition apply layerOpacity to the deck layer
      // alpha via the backend once the layer registry exposes per-layer props.
      recomputeShell();
    },

    zoomToLayer(id: string): void {
      // Fit-to-extent over the layer group's VISIBLE members (Glomar-safe: only the
      // cleared-visible set is consulted). Reads the production pdp's visible set for LIVE
      // geometry (membership is projection-coherent) — the projection's row geometry is
      // frozen on the firehose, so fitting to it would frame stale positions. Falls back to
      // a single-point fly for a one-member group. A removed/hidden/unknown group is a no-op.
      const pts: [number, number][] = [];
      for (const env of pdp.visibleEnvelopes()) {
        if (spineKeyFor(env) !== id) continue;
        const g = env.geometry;
        if (g && g.type === "Point") {
          const c = g.coordinates as [number, number];
          if (Array.isArray(c) && c.length >= 2) pts.push([c[0], c[1]]);
        }
      }
      if (pts.length === 0) return;
      if (pts.length === 1) {
        engine.setView({ longitude: pts[0][0], latitude: pts[0][1], zoom: Math.max(INITIAL_VIEW.zoom, 10) });
        return;
      }
      // Center on the group's bbox centroid; pick a zoom that frames its span.
      let minLon = pts[0][0], maxLon = pts[0][0], minLat = pts[0][1], maxLat = pts[0][1];
      for (const [lon, lat] of pts) {
        if (lon < minLon) minLon = lon;
        if (lon > maxLon) maxLon = lon;
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
      }
      const span = Math.max(maxLon - minLon, maxLat - minLat, 0.001);
      // Coarse span→zoom: a ~360° span ≈ z1, halving span ≈ +1 zoom (log2).
      const zoom = Math.min(14, Math.max(2, Math.floor(Math.log2(360 / span))));
      engine.setView({ longitude: (minLon + maxLon) / 2, latitude: (minLat + maxLat) / 2, zoom });
    },

    removeLayer(id: string): void {
      // Remove the layer group from the picture: flag it removed (its current AND
      // future members stay off the engine), drop it from the spine, and re-gate.
      // Reversible via the Catalog +Add (the group reappears as an addable leaf).
      layerRemoved.add(id);
      layerHidden.delete(id);
      applyLayerGate();
      rescanHiddenByFilter(); // layer state changed with no delta → refresh the fade counter O(N)
      recomputeShell();
    },

    addLayer(catalogLeafId: string): void {
      // +Add a Catalog leaf: clear any removed/hidden flag for that group so its
      // visible members render again, then re-gate + re-derive. The leaf id IS the
      // spine group key (catalog leaves are keyed by spineKeyFor) — re-add is
      // idempotent. Glomar-safe: only ever un-hides an already-cleared group.
      layerRemoved.delete(catalogLeafId);
      layerHidden.delete(catalogLeafId);
      applyLayerGate();
      rescanHiddenByFilter(); // layer state changed with no delta → refresh the fade counter O(N)
      recomputeShell();
    },

    runQuery(ast: unknown): void {
      // Parse the TypedQuery AST (the Throughline's { raw, term, intent }) and fan
      // ranked results across the rungs the host can answer FOR REAL: GO (locate a
      // visible entity), FILTER (re-scope by the term), OPEN (the fusion graph).
      // DO is folded client-side from `commands`; ASK is honest-stub (no faked AI).
      const q = (ast ?? {}) as { term?: unknown; raw?: unknown };
      const term = (typeof q.term === "string" ? q.term : typeof q.raw === "string" ? q.raw : "").trim();
      const out: QueryResult[] = [];
      if (term.length > 0) {
        const needle = term.toLowerCase();
        // GO — visible entities whose name/id matches (visible-only; a hidden object
        // is simply absent, never a counted gap — a fold of the one projection).
        for (const env of projection.bannerBasis()) {
          const name = String(env.properties.name ?? env.id);
          if (name.toLowerCase().includes(needle) || env.id.toLowerCase().includes(needle)) {
            out.push({ id: env.id, rung: "go", title: name, shortcut: "↵ fly" });
            if (out.length >= 8) break;
          }
        }
        // FILTER — re-scope the visible set to entities matching the term.
        out.push({ id: term, rung: "filter", title: `Filter to "${term}"`, shortcut: "#" });
        // ASK — honest preview row (the Glass Compiler is milestone-deferred; we do
        // NOT fabricate an AI answer). Surfaced so the rung is discoverable, inert.
        out.push({ id: `ask:${term}`, rung: "ask", title: `Ask — coming (Glass Compiler)`, shortcut: "?" });
      }
      // OPEN — the fusion graph is always summonable (the operator's one OPEN verb).
      out.push({ id: "fusion-graph", rung: "open", title: "Open fusion entity graph", shortcut: "⊞" });
      status.queryResults = out;
      emitStatus();
    },

    applyFilter(filterAst: unknown): void {
      // FILTER rung (fade-not-hide): set a predicate over the term; matching visible
      // entities stay, non-matching are dropped from the ENGINE only (kept in the PDP
      // registry → reversible) and counted in census.hiddenByFilter. A blank term
      // CLEARS the filter. Glomar: the denominator is the cleared-visible set; no
      // hidden object is ever counted, padlocked, or greyed.
      //
      // ISOLATE-A-CLASS (B1, fade-not-hide): an explicit `classification` field selects a
      // CLASSIFICATION BAND to FADE — entities whose security.classification matches the
      // chosen level get `false` (faded out of the engine, counted in hiddenByFilter) while
      // the rest stay. This is a pure in-place fade over the ALREADY-CLEARED VISIBLE set: it
      // NEVER removes an object from the registry and NEVER reveals a hidden one. Denial stays
      // Glomar — a withheld object is not in the visible set, so it can be neither faded nor
      // counted. The classification compare is over the visible env's OUTER marking only.
      const f = (filterAst ?? {}) as { term?: unknown; raw?: unknown; classification?: unknown };

      const cls = typeof f.classification === "string" ? f.classification.trim() : "";
      if (cls.length > 0) {
        // Normalise to the canonical band (case/space/hyphen) so "top secret" === "TOP_SECRET".
        const band = cls.toUpperCase().replace(/[\s-]+/g, "_");
        filterPredicate = (env: CanonicalEnvelope): boolean => {
          const envBand = String(env.security?.classification ?? "").toUpperCase().replace(/[\s-]+/g, "_");
          // Return FALSE for a MATCH ⇒ matching entities FADE (the isolated band dims out);
          // non-matching stay full-fidelity. Fade, never hide — they remain in the registry +
          // the cleared-visible denominator.
          return envBand !== band;
        };
        status.activeFilter = { label: `Fade class: ${band}`, chips: [band] };
        applyLayerGate();
        rescanHiddenByFilter(); // filter state changed with no delta → refresh the fade counter O(N)
        recomputeShell();
        return;
      }

      const term = (typeof f.term === "string" ? f.term : typeof f.raw === "string" ? f.raw : "").trim();
      if (term.length === 0) {
        filterPredicate = null;
        status.activeFilter = null;
      } else {
        const needle = term.toLowerCase();
        filterPredicate = (env: CanonicalEnvelope): boolean => {
          const name = String(env.properties.name ?? env.id).toLowerCase();
          const aff = String(env.properties.affiliation ?? "").toLowerCase();
          const kind = env.kind.toLowerCase();
          return name.includes(needle) || aff.includes(needle) || kind.includes(needle);
        };
        status.activeFilter = { label: `Filter: ${term}`, chips: [term] };
      }
      applyLayerGate();
      rescanHiddenByFilter(); // filter state changed with no delta → refresh the fade counter O(N)
      recomputeShell();
    },

    stageAction(card: unknown): void {
      // DO rung action staging. Most folded `commands` (recenter, symbology, render profiles,
      // sign-out) run client-side via their own `run()`. The MARKER-AUTHOR action (S3) is a REAL
      // host-side verb: `{kind:"waypoint"}` ARMS map placement (the next non-shift click drops a
      // typed, fail-closed-classified waypoint via the bus/projection seam + S1 egress). Other
      // kinds remain honest no-ops (no faked "staged" confirmation). Spec §2.6.
      const kind = card && typeof card === "object" && "kind" in card ? String((card as { kind: unknown }).kind) : "";
      if (kind === "waypoint") {
        setMarkerPlacement(true);
        pushToast({
          title: "MARKER · ARM",
          body: "Click the map to drop a waypoint at your working classification.",
          tone: "info",
        });
        return;
      }
      // `evac` / `settings` and any other kind: honest no-op (no host-side commit path wired
      // today). The rail buttons for these are intentionally inert beyond this toast-free no-op;
      // the evac 9-line dispatch is human-gated + milestone-deferred (see FunctionRail relabel).
    },

    openGraph(id: string): void {
      // Summon the GraphView for the entity, FROM the Ledger. The React shell renders
      // the overlay off the onGraph seam (claims the one heavy panel React-side).
      emitGraph(id);
    },

    markAlertRead(id: string): void {
      // Acknowledge ONE alert (mark-read-on-click). A UI acknowledgement only — it never
      // touches pdp/kas/projection. Guard: only mark an id that is actually in the cleared-
      // visible alert log (so we never fabricate read-state for a hidden/unknown id — Glomar).
      // Persisted per user by alertAcks (display state; survives reload).
      if (!status.alertLog.some((a) => a.id === id)) return;
      if (!alertAcks.markRead(id)) return;
      recomputeAlertLog();
      emitStatus();
    },

    dismissAlert(id: string): void {
      // Dismiss ONE alert from the operator's log (the per-row ×, self-scope). Same Glomar
      // guard as markAlertRead: only an id already in the cleared-visible log can be
      // dismissed. A VIEW dismiss, NOT a denial: the object stays in the registry + on the
      // map; a re-emit re-surfaces it (unread). Persisted per user by alertAcks.
      if (!status.alertLog.some((a) => a.id === id)) return;
      if (!alertAcks.dismiss(id)) return;
      recomputeAlertLog();
      recomputeShell(); // the verdict excludes dismissed alerts — this may quiet the glyph
      emitStatus();
    },

    clearAllAlerts(): void {
      // Dismiss EVERY currently-visible alert from the operator's log (self-scope). This is a
      // VIEW dismiss, NOT a denial: the underlying alert objects stay in the registry + on the
      // map; a re-emit re-surfaces the alert (unread). We only ever clear ids the operator can
      // already see (the cleared-visible log) — never a hidden one (Glomar). Persisted per
      // user by alertAcks.
      if (status.alertLog.length === 0) return;
      alertAcks.clearAll(status.alertLog.map((row) => row.id));
      recomputeAlertLog();
      recomputeShell(); // the verdict excludes dismissed alerts — clearing quiets the glyph
      emitStatus();
    },

    setShareLocation(on: boolean): Promise<boolean> {
      // OFF is synchronous + immediate (fail-closed): the watch + timer are cleared and
      // the captured fix dropped before this returns — nothing publishes after OFF.
      if (!on) {
        ownPosition.disable("Location sharing stopped. Nothing further is published.");
        return Promise.resolve(false);
      }
      // ON runs the DOUBLE-CONSENT flow (our dialog → the browser geolocation prompt);
      // resolves the resulting state (false on any refusal/error — fail-closed).
      return ownPosition.enable();
    },

    callForEvac(): Promise<void> {
      return callForEvac();
    },

    setLayout(layout: LayoutRecipe): void {
      // Device-class layout recipe switch (static recompose). Records the recipe for
      // SettingsDrawer checkmarks + GutterFrame, then re-fits the map on the polled
      // nudge schedule so the WebGL2 buffer tracks the grid grow (§4.3).
      status.layout = layout;
      emitStatus();
      scheduleRefit();
    },
  };
}
